import { confirmCost } from "@/components/cost-confirm";
import { useState, useEffect, useRef } from "react";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Area, AreaChart, LineChart, Line, Bar, BarChart, LabelList, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip } from "recharts";
import { startGoogleConnect } from "@/lib/google-connect";
import {
  BarChart3, Globe, Users, MousePointerClick, Eye, TrendingUp, TrendingDown,
  Search, MapPin, Sparkles, Link2, ArrowDownRight, ArrowUpRight, Plug, Settings2,
  Gauge, Target, Radar, Split, Activity, AlertTriangle, CheckCircle2, Zap,
  Trophy, FileText, Repeat, Layers, Lock,
  Lightbulb, CalendarPlus, RefreshCw, Loader2, Check, CalendarRange, Plus,
  ExternalLink,
} from "lucide-react";

type Delta = number;
type TrafficData = {
  gsc: { connected: boolean; siteUrl?: string };
  ga: { connected: boolean; propertyId?: string };
  brandTerm?: string;
  kpis?: {
    totalUsers: number; organicUsers: number; sessions: number; impressions: number; clicks: number; ctr: number; avgPosition: number;
    newUsers: number; returningUsers: number; engagementRate: number; avgEngagementSec: number;
    dUsers: Delta; dOrganic: Delta; dClicks: Delta; dImpressions: Delta; dPosition: Delta; dCtr: Delta;
  };
  trend?: { date: string; clicks: number; impressions: number; ctr: number; position: number; users: number }[];
  filter?: string;
  filterDim?: string;
  channels?: { source: string; users: number; pct: number }[];
  sources?: { source: string; users: number; pct: number }[];
  aiTraffic?: { source: string; users: number }[];
  aiTrend?: ({ date: string; users: number } & Partial<Record<"ChatGPT" | "Perplexity" | "Gemini" | "Claude" | "Copilot" | "You.com", number>>)[];
  aiByEngine?: { engine: string; users: number; sessions: number; conversions: number }[];
  aiLandingPages?: { page: string; engine: string; users: number; conversions: number }[];
  aiSummary?: { totalUsers: number; totalSessions: number; totalConversions: number; prevUsers: number; changePct: number };
  topPages?: { page: string; clicks: number; impressions: number; ctr: number; position: number }[];
  landingPages?: { page: string; users: number }[];
  decliningPages?: { page: string; current: number; previous: number; changePct: number }[];
  risingPages?: { page: string; current: number; previous: number; changePct: number }[];
  queries?: { query: string; impressions: number; clicks: number; ctr: number; position: number }[];
  brand?: { term: string; brandClicks: number; brandImpr: number; nonBrandClicks: number; nonBrandImpr: number };
  newVsReturning?: { newUsers: number; returningUsers: number };
  regions?: { country: string; users: number }[];
  cities?: { city: string; users: number }[];
  opportunities?: {
    firstPagePush: { query: string; position: number; impressions: number; clicks: number }[];
    ctrOpportunity: { query: string; position: number; ctr: number; expectedCtr: number; impressions: number; clicks: number; potentialClicks: number }[];
    impressionGoldmine: { query: string; impressions: number; clicks: number; ctr: number; position: number }[];
  };
  keywordRadar?: {
    rising: { query: string; prev: number; cur: number }[];
    falling: { query: string; prev: number; cur: number }[];
    gained: { query: string; cur: number }[];
    lost: { query: string; prev: number }[];
  };
  cannibalization?: { query: string; pages: { page: string; clicks: number; impressions: number; position: number }[] }[];
  momentum?: { score: number; direction: "up" | "down" | "stable"; parts: { clicks: number; position: number; impressions: number; ctr: number } };
  anomaly?: { status: "normal" | "spike" | "drop"; today: number; avg: number; detail: string };
  alerts?: { level: "critical" | "warning" | "good"; title: string; detail: string }[];
};

const fmt = (n: number) => (n >= 10000 ? `${(n / 1000).toFixed(0)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));
const pct = (n: number) => `%${(n * 100).toFixed(1)}`;
const shortUrl = (u: string) => u.replace(/^https?:\/\/[^/]+/, "").replace(/\/$/, "") || "/";

const C = { cyan: "#e83e3c", green: "#12b76a", red: "#f04438", amber: "#f79009", purple: "#e83e3c", blue: "#ec6461", pink: "#f18e8b" };

function DeltaBadge({ value, invert, suffix = "%" }: { value?: number; invert?: boolean; suffix?: string }) {
  if (value == null || !isFinite(value) || Math.abs(value) < 0.5) return <span className="text-[11px] text-muted-foreground">—</span>;
  const good = invert ? value < 0 : value > 0;
  const color = good ? C.green : C.red;
  const Icon = value > 0 ? ArrowUpRight : ArrowDownRight;
  return <span className="text-[11px] inline-flex items-center gap-0.5 font-medium" style={{ color }}><Icon className="w-3 h-3" />{value > 0 ? "+" : ""}{Math.round(value)}{suffix}</span>;
}

function Section({ id, title, icon: Icon, desc, children, accent = C.cyan }: { id: string; title: string; icon: typeof BarChart3; desc?: string; children: React.ReactNode; accent?: string }) {
  return (
    <section id={id} style={{ scrollMarginTop: 16 }} className="mb-7">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}1f` }}><Icon className="w-4 h-4" style={{ color: accent }} /></div>
        <div>
          <h2 className="text-base font-bold leading-tight">{title}</h2>
          {desc && <p className="text-xs text-muted-foreground">{desc}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function Panel({ title, icon: Icon, children, connected, hint, color = C.cyan, className }: { title: string; icon: typeof BarChart3; children?: React.ReactNode; connected: boolean; hint?: string; color?: string; className?: string }) {
  return (
    <Card className={className}><CardContent className="p-4">
      <div className="flex items-center gap-2 mb-3"><Icon className="w-4 h-4" style={{ color }} /><span className="font-semibold text-sm">{title}</span></div>
      {connected ? children : <div className="text-xs text-muted-foreground py-8 text-center">{hint ?? "Bağlantı kurulunca bu veriler gelecek."}</div>}
    </CardContent></Card>
  );
}

function Row({ label, value, truncate, mono, href }: { label: React.ReactNode; value: React.ReactNode; truncate?: boolean; mono?: boolean; href?: string }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className={truncate ? "truncate flex-1 min-w-0" : ""} title={typeof label === "string" ? label : undefined}>{label}</span>
      <span className={`text-muted-foreground shrink-0 flex items-center gap-1.5 ${mono ? "tabular-nums" : ""}`}>
        {value}
        {href && <SayfayaGit href={href} />}
      </span>
    </div>
  );
}

// Satırın yanındaki "sayfaya git" oku — gerçek sayfayı yeni sekmede açar
function SayfayaGit({ href }: { href: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" title={`Sayfayı aç: ${href}`}
      className="shrink-0 text-muted-foreground/50 hover:text-foreground transition-colors">
      <ExternalLink className="w-3.5 h-3.5" />
    </a>
  );
}

function BarRow({ label, value, max, color, suffix }: { label: string; value: number; max: number; color: string; suffix?: string }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2 text-xs"><span className="truncate flex-1 min-w-0" title={label}>{label}</span><span className="text-muted-foreground tabular-nums shrink-0">{fmt(value)}{suffix ?? ""}</span></div>
      <div className="h-1.5 rounded-full bg-black/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.max(2, (value / (max || 1)) * 100)}%`, background: color }} /></div>
    </div>
  );
}

// GSC arayüzü benzeri tablo: regex filtre + sıralanabilir sütunlar (Tıklama/Gösterim/CTR/Pozisyon)
type GscRow = { label: string; clicks: number; impressions: number; ctr: number; position: number };
function GscTable({ rows, labelHeader }: { rows: GscRow[]; labelHeader: string }) {
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState<keyof Omit<GscRow, "label">>("clicks");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  let re: RegExp | null = null, reErr = false;
  try { re = filter.trim() ? new RegExp(filter, "i") : null; } catch { reErr = true; }
  const filtered = re ? rows.filter((r) => re!.test(r.label)) : rows;
  const sorted = [...filtered].sort((a, b) => (a[sortKey] - b[sortKey]) * (dir === "asc" ? 1 : -1));
  const tot = filtered.reduce((s, r) => ({ clicks: s.clicks + r.clicks, impressions: s.impressions + r.impressions }), { clicks: 0, impressions: 0 });
  const SortTh = ({ k, label }: { k: keyof Omit<GscRow, "label">; label: string }) => (
    <th className="py-2 px-2 text-right whitespace-nowrap">
      <button onClick={() => { if (sortKey === k) setDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(k); setDir(k === "position" ? "asc" : "desc"); } }} className="inline-flex items-center gap-1 hover:text-foreground transition">
        {label}{sortKey === k && <span className="text-[9px]">{dir === "asc" ? "▲" : "▼"}</span>}
      </button>
    </th>
  );
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center gap-1.5 flex-1 border rounded px-2 py-1" style={{ borderColor: reErr ? C.red : "rgba(0,0,0,0.1)" }}>
          <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Regex ile filtrele (ör. tahta|böcek, ^hamam, fiyat$)" className="flex-1 bg-transparent text-xs outline-none min-w-0" />
          {filter && <button onClick={() => setFilter("")} className="text-muted-foreground hover:text-foreground text-xs shrink-0">✕</button>}
        </div>
        <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">{fmt(sorted.length)} satır · {fmt(tot.clicks)} tık · {fmt(tot.impressions)} gst</span>
      </div>
      {reErr && <p className="text-[11px] text-red-400 mb-1">Geçersiz regex</p>}
      <div className="overflow-x-auto max-h-[520px] overflow-y-auto rounded border" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
        <table className="w-full text-sm min-w-[560px]">
          <thead className="sticky top-0 z-10 bg-white dark:bg-gray-900">
            <tr className="text-xs text-muted-foreground text-left border-b">
              <th className="py-2 px-2">{labelHeader}</th>
              <SortTh k="clicks" label="Tıklama" /><SortTh k="impressions" label="Gösterim" /><SortTh k="ctr" label="CTR" /><SortTh k="position" label="Pozisyon" />
            </tr>
          </thead>
          <tbody>{sorted.slice(0, 250).map((r, i) => (
            <tr key={i} className="border-b last:border-0 hover:bg-black/[0.02]">
              <td className="py-2 px-2 max-w-[420px] truncate" title={r.label}>{r.label}</td>
              <td className="py-2 px-2 text-right tabular-nums">{fmt(r.clicks)}</td>
              <td className="py-2 px-2 text-right tabular-nums text-muted-foreground">{fmt(r.impressions)}</td>
              <td className="py-2 px-2 text-right tabular-nums text-muted-foreground">{pct(r.ctr)}</td>
              <td className="py-2 px-2 text-right tabular-nums font-medium">{r.position.toFixed(1)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

type OppRec = {
  title: string; type: "new" | "expand"; relatedPageUrl: string | null; clusterName: string | null;
  rationale: string; priority: "high" | "medium" | "low"; estimatedImpressions: number; estimatedVolume: number;
  targetKeywords: { keyword: string; impressions: number; position: number; clicks: number; volume: number; competition: number }[];
};
type OppResp = { gscConnected: boolean; generatedAt: string | null; recommendations: OppRec[]; meta: { candidateCount?: number; pageCount?: number; gscSite?: string } };

type VisBrand = { name: string; mentions: number; isUs: boolean };
type VisPrompt = { topic: string; model: string; mentioned: boolean; rank: number | null; brands: string[] };
type VisByModel = { model: string; visibilityScore: number; mentionRate: number; topicCount: number };
type VisLatest = {
  runAt: string; brand: string; visibilityScore: number; sov: number; mentionRate: number;
  topBrands: VisBrand[]; prompts: VisPrompt[]; byModel: VisByModel[];
  meta: { avgRank: number; topicCount: number; models: string[]; lang?: string };
};
type VisResp = { configured: boolean; latest: VisLatest | null; trend: { runAt: string; visibilityScore: number; sov: number }[] };

type OvDomain = { domain: string; mentions: number; aiSearchVolume: number; isUs: boolean };
type OvQuestion = { question: string; aiSearchVolume: number; platform: string; cited: boolean; sources: string[] };
type OvTopic = { topic: string; aiSearchVolume: number; ourMentions: number; totalMentions: number };
type OvPlacement = { question: string; aiSearchVolume: number; url: string; title: string; position: number; totalSources: number; platform: string };
type OvEngine = { question: string; aiSearchVolume: number; google: boolean; chatgpt: boolean; chatgptSource?: boolean; gemini: boolean; claude: boolean; perplexity: boolean };
type OvLatest = { runAt: string; ourDomain: string; share: number; totalQuestions: number; totalVolume: number; topDomains: OvDomain[]; questions: OvQuestion[]; byTopic: OvTopic[]; placements: OvPlacement[]; placementsTotal: number; engineMatrix: OvEngine[]; meta: { topicCount: number; ourMentions: number; totalMentions: number } };
type OvResp = { configured: boolean; latest: OvLatest | null; running?: boolean; runError?: string | null };

type PosItem = { question: string; aiSearchVolume: number; position: number; totalSources: number; url: string; above: { domain: string; title: string }[]; tips: string[] };
type PosResp = { configured: boolean; generatedAt: string | null; items: PosItem[] };

// GSC arayüzü gibi çoklu filtre (AND ile birleşir)
type GscFilter = { dim: "query" | "page"; op: string; expr: string };
const MATCH_OPS = [
  { op: "contains", q: "şunu içeren", p: "şunu içeren URL'ler" },
  { op: "notContains", q: "şunu içermeyen", p: "şunu içermeyen URL'ler" },
  { op: "equals", q: "tam sorgu", p: "tam URL" },
  { op: "includingRegex", q: "regex eşleşir", p: "regex eşleşir" },
  { op: "excludingRegex", q: "regex eşleşmez", p: "regex eşleşmez" },
];
const opLabel = (f: GscFilter) => MATCH_OPS.find((m) => m.op === f.op)?.[f.dim === "query" ? "q" : "p"] ?? f.op;

type SavedRegex = { id: number; name: string; dim: "query" | "page"; op: string; expr: string };

function GscFilterBar({ domainId, filters, onChange, fetching }: { domainId: number | null; filters: GscFilter[]; onChange: (f: GscFilter[]) => void; fetching: boolean }) {
  const [dim, setDim] = useState<"query" | "page">("query");
  const [op, setOp] = useState("contains");
  const [expr, setExpr] = useState("");
  const [showSaved, setShowSaved] = useState(false);
  const queryClient = useQueryClient();
  const add = () => { if (!expr.trim()) return; onChange([...filters, { dim, op, expr: expr.trim() }]); setExpr(""); };
  const sel = "bg-white text-gray-800 border rounded-lg text-sm px-2 py-1.5 outline-none border-black/10 dark:bg-gray-800 dark:text-white/90 dark:border-gray-700";

  // ── Kayıtlı regexler — kalıbı bir kez kaydet, her seferinde tek tıkla uygula ──
  const { data: savedResp } = useQuery({
    queryKey: ["saved-regexes", domainId],
    enabled: !!domainId,
    queryFn: async (): Promise<{ regexes: SavedRegex[] }> => {
      try { const r = await fetch(`/api/domains/${domainId}/saved-regexes`); return r.ok ? await r.json() : { regexes: [] }; } catch { return { regexes: [] }; }
    },
  });
  const saved = savedResp?.regexes ?? [];
  const saveCurrent = async () => {
    if (!domainId || !expr.trim()) return;
    const name = window.prompt("Bu filtreye bir isim ver (ör. Ünlü sayfaları hariç):");
    if (!name?.trim()) return;
    const r = await fetch(`/api/domains/${domainId}/saved-regexes`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ name: name.trim(), dim, op, expr: expr.trim() }),
    });
    if (!r.ok) { window.alert((await r.json().catch(() => ({}))).error ?? "Kaydedilemedi"); return; }
    queryClient.invalidateQueries({ queryKey: ["saved-regexes", domainId] });
    setShowSaved(true);
  };
  const deleteSaved = async (id: number) => {
    if (!domainId) return;
    await fetch(`/api/domains/${domainId}/saved-regexes/${id}`, { method: "DELETE" });
    queryClient.invalidateQueries({ queryKey: ["saved-regexes", domainId] });
  };
  const applySaved = (s: SavedRegex) => {
    // Aynı filtre zaten uygulanmışsa yineleme
    if (filters.some((f) => f.dim === s.dim && f.op === s.op && f.expr === s.expr)) return;
    onChange([...filters, { dim: s.dim, op: s.op, expr: s.expr }]);
  };

  return (
    <div className="space-y-2 mb-4">
      {filters.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Filtreler (hepsi birlikte uygulanır):</span>
          {filters.map((f, i) => (
            <span key={i} className="text-xs px-2 py-1 rounded-full inline-flex items-center gap-1.5 max-w-[420px]" style={{ background: `${C.cyan}18`, color: C.cyan }}>
              <span className="truncate">{f.dim === "query" ? "Sorgu" : "Sayfa"} · {opLabel(f)}: <b>{f.expr}</b></span>
              <button onClick={() => onChange(filters.filter((_, idx) => idx !== i))} className="hover:opacity-60 ml-0.5 shrink-0">✕</button>
            </span>
          ))}
          {fetching && <span className="text-xs text-muted-foreground">· yenileniyor…</span>}
          <button onClick={() => onChange([])} className="text-xs text-muted-foreground hover:text-foreground underline ml-1">tümünü temizle</button>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <select value={dim} onChange={(e) => setDim(e.target.value as "query" | "page")} className={sel}>
          <option value="query">Anahtar kelime (sorgu)</option>
          <option value="page">Sayfa (URL)</option>
        </select>
        <select value={op} onChange={(e) => setOp(e.target.value)} className={sel}>
          {MATCH_OPS.map((m) => <option key={m.op} value={m.op}>{dim === "query" ? m.q : m.p}</option>)}
        </select>
        <div className="flex items-center gap-1.5 flex-1 min-w-[200px] border rounded-lg px-2 py-1.5 bg-white border-black/10 dark:bg-gray-800 dark:border-gray-700">
          <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input value={expr} onChange={(e) => setExpr(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder={op.includes("Regex") ? "Regex — örn. dolly|laura|miley, ^hamam, fiyat$" : "değer gir"} className="flex-1 bg-transparent text-sm outline-none min-w-0 text-gray-800 placeholder:text-gray-400 dark:text-white/90 dark:placeholder:text-gray-500" />
        </div>
        <Button size="sm" onClick={add} className="gap-1.5 shrink-0"><Plus className="w-3.5 h-3.5" /> Filtre ekle</Button>
        <Button size="sm" variant="outline" onClick={saveCurrent} disabled={!expr.trim()} title="Yazdığın kalıbı isim vererek kaydet" className="gap-1.5 shrink-0">💾 Kaydet</Button>
        <Button size="sm" variant={showSaved ? "secondary" : "outline"} onClick={() => setShowSaved(!showSaved)} className="gap-1.5 shrink-0">
          📌 Kayıtlı Regexlerim{saved.length > 0 ? ` (${saved.length})` : ""}
        </Button>
      </div>
      {showSaved && (
        <div className="rounded-xl border p-3 space-y-1.5" style={{ borderColor: "rgba(0,0,0,0.1)", background: "#fbfcfe" }}>
          {saved.length === 0 && <div className="text-xs text-muted-foreground">Henüz kayıtlı regex yok — yukarıya kalıbı yazıp 💾 Kaydet'e bas.</div>}
          {saved.map((s) => {
            const active = filters.some((f) => f.dim === s.dim && f.op === s.op && f.expr === s.expr);
            return (
              <div key={s.id} className="flex items-center gap-2 text-xs">
                <button onClick={() => applySaved(s)} disabled={active}
                  className="px-2 py-0.5 rounded-md font-medium shrink-0 transition disabled:opacity-50"
                  style={active ? { background: "rgba(34,197,94,0.12)", color: "#039855" } : { background: `${C.cyan}15`, color: "#b12220" }}>
                  {active ? "✓ uygulandı" : "Uygula"}
                </button>
                <b className="shrink-0">{s.name}</b>
                <span className="text-muted-foreground shrink-0">· {s.dim === "query" ? "Sorgu" : "Sayfa"} · {opLabel(s as GscFilter)}</span>
                <code className="text-muted-foreground truncate flex-1" title={s.expr}>{s.expr}</code>
                <button onClick={() => { if (window.confirm(`"${s.name}" silinsin mi?`)) deleteSaved(s.id); }} className="text-red-400 hover:text-red-500 shrink-0">🗑</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const NAV_TRAFFIC = [
  { id: "ozet", label: "Özet" },
  { id: "genel", label: "Genel Bakış" },
  { id: "kanallar", label: "Trafik & Kanallar" },
  { id: "bolgeler", label: "Bölgeler" },
  { id: "sayfalar", label: "Sayfalar" },
  { id: "kelimeler", label: "Anahtar Kelimeler" },
  { id: "firsat", label: "Fırsat Motoru" },
  { id: "radar", label: "Keyword Radar" },
  { id: "kanibal", label: "Kanibalizasyon" },
];
// AI Gelişim Karnesi tipi + başarı tablosu satırı (grid'e 4 hücre basar)
type AiGrowth = {
  hasData: boolean;
  start: { date: string | null; trafficMonth: string | null; aiUsersMonthly: number; placements: number; share: number; visibilityScore: number; enginesVisible: number };
  now: { date: string | null; trafficMonth: string | null; aiUsersMonthly: number; placements: number; share: number; visibilityScore: number; enginesVisible: number };
  highlights: { newQuestions: number; trafficMultiplier: number | null; trafficGrowthPct: number | null; placementGrowthPct: number | null; scoreDelta: number };
  monthlyAiUsers: { month: string; users: number }[];
  yearlyAiUsers?: { year: string; users: number; partial: boolean }[];
  yoy?: { prevYear: string; prevUsers: number; curYear: string; curUsers: number; curPartial: boolean; pct: number | null } | null;
  scoreTrend: { date: string; score: number; sov: number }[];
  placementTrend: { date: string; placements: number; share: number }[];
  successTable: { question: string; aiSearchVolume: number; position: number; totalSources: number; url: string }[];
};
function FragmentRow({ s, R }: { s: AiGrowth["successTable"][number]; R: string }) {
  // Pozisyon barı: 1. sıra = dolu bar (taslaktaki gibi soldan dolar)
  const strength = s.totalSources > 0 ? Math.max(0.08, (s.totalSources - s.position + 1) / s.totalSources) : 0.5;
  return (<>
    <div className="px-3 py-1.5 rounded-full border truncate" style={{ borderColor: "rgba(0,0,0,0.15)" }} title={s.question}>{s.question}</div>
    <div className="px-3 py-1.5 rounded-full border text-center tabular-nums" style={{ borderColor: "rgba(0,0,0,0.15)" }}>{s.aiSearchVolume >= 1000 ? `${(s.aiSearchVolume / 1000).toFixed(1)}k` : s.aiSearchVolume}</div>
    <div className="h-2.5 rounded-full" style={{ background: "rgba(0,0,0,0.06)" }}>
      <div className="h-2.5 rounded-full" style={{ width: `${strength * 100}%`, background: R }} />
    </div>
    <div className="text-right font-bold tabular-nums whitespace-nowrap" style={{ color: R }}>{s.position}. sıra</div>
  </>);
}

const NAV_AI = [
  { id: "ai-growth", label: "🚀 AI Karnesi" },
  { id: "ai-traffic", label: "AI Trafiği" },
  { id: "ai-overview", label: "Google AI Overview" },
  { id: "ai-visibility", label: "AI Visibility" },
  { id: "ai-sov", label: "Share of Voice" },
  { id: "ai-alerts", label: "AI SEO Alerts" },
];

export default function TrafficAnalysis() {
  const { data: domains } = useListDomains();
  const [activeDomainId, setActiveDomainId] = useActiveDomain(domains);
  // Sayfa listelerindeki "sayfaya git" oku: GA/GSC path döndürür (/blog/...),
  // gerçek adres = aktif sitenin kökü + path
  const siteKoku = (() => {
    const u = (domains ?? []).find((d) => d.id === activeDomainId)?.url ?? "";
    if (!u) return "";
    try { return new URL(/^https?:\/\//.test(u) ? u : `https://${u}`).origin; } catch { return ""; }
  })();
  const sayfaLinki = (p: string): string | undefined =>
    /^https?:\/\//.test(p) ? p : siteKoku && p.startsWith("/") ? `${siteKoku}${p}` : undefined;
  const [view, setView] = useState<"traffic" | "ai">("traffic");
  const [placeSort, setPlaceSort] = useState<"volume" | "position">("volume");
  const [range, setRange] = useState("28d");
  const [kwView, setKwView] = useState<"query" | "page">("query");
  const [filters, setFilters] = useState<GscFilter[]>([]);
  const filterKey = JSON.stringify(filters);
  const [metrics, setMetrics] = useState<Set<string>>(new Set(["clicks", "impressions"]));
  const toggleMetric = (m: string) => setMetrics((s) => { const n = new Set(s); n.has(m) ? n.delete(m) : n.add(m); return n.size ? n : s; });
  const [aiEng, setAiEng] = useState<Set<string>>(new Set(["ChatGPT", "Perplexity", "Gemini", "Claude", "Copilot", "You.com"]));
  const toggleAiEng = (e: string) => setAiEng((s) => { const n = new Set(s); n.has(e) ? n.delete(e) : n.add(e); return n.size ? n : s; });
  const [aiMarket, setAiMarket] = useState<"auto" | "tr" | "en" | "ar">("auto");
  const [aiGrTf, setAiGrTf] = useState<"monthly" | "yearly">("yearly"); // AI Karnesi trafik grafiği: yıllık varsayılan (karne mantığı)

  const { data, isFetching } = useQuery({
    queryKey: ["traffic", activeDomainId, range, filterKey],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<TrafficData> => {
      try {
        const r = await fetch(`/api/domains/${activeDomainId}/traffic?range=${range}&filters=${encodeURIComponent(filterKey)}`);
        if (!r.ok) return { gsc: { connected: false }, ga: { connected: false } };
        return (await r.json()) as TrafficData;
      } catch { return { gsc: { connected: false }, ga: { connected: false } }; }
    },
  });
  const t = data ?? { gsc: { connected: false }, ga: { connected: false } };
  const gsc = t.gsc.connected;
  const ga = t.ga.connected;
  const anyConnected = gsc || ga;
  const k = t.kpis;

  const connect = (provider: "gsc" | "ga") => {
    if (!activeDomainId) return;
    // Hata YUTULMASIN: bilet reddedilirse (ör. site sahipliği uyuşmadı) kullanıcı sebebini görsün
    startGoogleConnect(activeDomainId).catch((e) => alert(String(e instanceof Error ? e.message : e)));
  };

  const queryClient = useQueryClient();
  type Options = { connected: boolean; email?: string; gscSites?: { siteUrl: string }[]; gaProperties?: { propertyId: string; displayName: string; account: string }[]; current?: { gscSiteUrl: string | null; gaPropertyId: string | null }; authBroken?: string | null };
  const { data: options } = useQuery({
    queryKey: ["google-options", activeDomainId],
    enabled: !!activeDomainId && (gsc || ga),
    queryFn: async (): Promise<Options> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/google-options`); return r.ok ? await r.json() : { connected: false }; }
      catch { return { connected: false }; }
    },
  });
  const reconnect = () => { if (activeDomainId) startGoogleConnect(activeDomainId).catch((e) => alert(String(e instanceof Error ? e.message : e))); };
  const setSelection = async (patch: { gscSiteUrl?: string; gaPropertyId?: string }) => {
    if (!activeDomainId) return;
    await fetch(`/api/domains/${activeDomainId}/google-selection`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(patch) });
    queryClient.invalidateQueries({ queryKey: ["traffic", activeDomainId] });
    queryClient.invalidateQueries({ queryKey: ["google-options", activeDomainId] });
  };

  // ── AI Gelişim Karnesi (Başlangıç→Sonuç) ──
  const { data: aiGrowth } = useQuery({
    queryKey: ["ai-growth", activeDomainId],
    enabled: !!activeDomainId && view === "ai",
    queryFn: async (): Promise<AiGrowth | null> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/ai-growth`); return r.ok ? await r.json() : null; } catch { return null; }
    },
  });

  // ── AI Visibility (DataForSEO) ──
  const { data: vis } = useQuery({
    queryKey: ["ai-visibility", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<VisResp> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/ai-visibility`); return r.ok ? await r.json() : { configured: false, latest: null, trend: [] }; }
      catch { return { configured: false, latest: null, trend: [] }; }
    },
  });
  const runVis = useMutation({
    mutationFn: async (): Promise<VisResp> => {
      const r = await fetch(`/api/domains/${activeDomainId}/ai-visibility`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ market: aiMarket }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Analiz hatası");
      return r.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ai-visibility", activeDomainId] }),
  });

  // ── Google AI Overview (DataForSEO llm_mentions, ~$1, ayrı buton) ──
  const { data: ov } = useQuery({
    queryKey: ["ai-overview", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<OvResp> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/ai-overview`); return r.ok ? await r.json() : { configured: false, latest: null }; }
      catch { return { configured: false, latest: null }; }
    },
    refetchInterval: (q) => (q.state.data?.running ? 5000 : false),
  });
  const runOv = useMutation({
    mutationFn: async (): Promise<{ started: boolean }> => {
      const r = await fetch(`/api/domains/${activeDomainId}/ai-overview`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ market: aiMarket }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Analiz hatası");
      return r.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ai-overview", activeDomainId] }),
  });
  // Analiz arka planda bitince (running true→false) bağlı panelleri tazele
  const ovWasRunning = useRef(false);
  useEffect(() => {
    if (ovWasRunning.current && ov && !ov.running) {
      queryClient.invalidateQueries({ queryKey: ["ai-overview", activeDomainId] });
      queryClient.invalidateQueries({ queryKey: ["ai-prompt-inference", activeDomainId] });
    }
    ovWasRunning.current = !!ov?.running;
  }, [ov?.running]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── AI Prompt Tahmini: placements (AI Overview) + GSC sayfa→sorgu (maliyetsiz — DB + GSC) ──
  type PromptInf = {
    configured: boolean;
    placements: { question: string; aiSearchVolume: number; url: string; position: number; totalSources: number; platform: string }[];
    gscPageQueries: Record<string, { query: string; clicks: number; impressions: number }[]>;
    ovRunAt: string | null;
  };
  const { data: promptInf } = useQuery({
    queryKey: ["ai-prompt-inference", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<PromptInf | null> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/ai-prompt-inference`); return r.ok ? await r.json() : null; } catch { return null; }
    },
  });

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const channels = t.channels ?? t.sources ?? [];
  const maxCh = Math.max(...channels.map((c) => c.users), 1);
  const brand = t.brand;
  const brandTotal = brand ? brand.brandClicks + brand.nonBrandClicks : 0;
  const mom = t.momentum;

  return (
    <div className="px-4 sm:px-6 py-6 mx-auto w-full max-w-[1500px]">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2 text-sm" style={{ color: C.cyan }}><BarChart3 className="w-4 h-4" /> Trafik Analizi</div>
          <h1 className="text-3xl font-bold text-foreground mt-0.5">Trafik & Performans</h1>
          <p className="text-sm text-muted-foreground">Search Console + Analytics verisiyle uçtan uca SEO analizi {isFetching && <span style={{ color: C.cyan }}>· yenileniyor…</span>}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-[150px] gap-2"><CalendarRange className="w-4 h-4" /><SelectValue /></SelectTrigger>
            <SelectContent>
              {[{ v: "24h", l: "Son 24 saat" }, { v: "7d", l: "Son 7 gün" }, { v: "28d", l: "Son 28 gün" }, { v: "3m", l: "Son 3 ay" }, { v: "6m", l: "Son 6 ay" }, { v: "12m", l: "Son 12 ay" }, { v: "16m", l: "Son 16 ay" }].map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bağlantı kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {[
          { key: "gsc" as const, name: "Google Search Console", desc: "Gösterim, tıklama, pozisyon, sorgular", connected: gsc, sub: t.gsc.siteUrl, color: "#4285F4" },
          { key: "ga" as const, name: "Google Analytics (GA4)", desc: "Kullanıcı, kaynak, bölge, AI trafiği", connected: ga, sub: t.ga.propertyId, color: "#E37400" },
        ].map((c) => (
          <Card key={c.key} style={{ borderColor: c.connected ? `${c.color}66` : undefined }}>
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${c.color}1f` }}><Plug className="w-5 h-5" style={{ color: c.color }} /></div>
                <div className="min-w-0">
                  <div className="font-medium text-sm">{c.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.connected ? (c.sub ?? "Bağlı") : c.desc}</div>
                </div>
              </div>
              {c.connected
                ? <span className="text-xs px-2.5 py-1 rounded-full shrink-0" style={{ background: "#12b76a22", color: C.green }}>Bağlı</span>
                : <Button size="sm" variant="outline" className="shrink-0 gap-1.5" onClick={() => connect(c.key)}><Plug className="w-3.5 h-3.5" /> Bağla</Button>}
            </CardContent>
          </Card>
        ))}
      </div>

      {options?.authBroken && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3" style={{ borderColor: "#f7b6b4", background: "#fdecea" }}>
          <span className="text-[12px] font-medium" style={{ color: "#b12220" }}>
            ⚠ Google bağlantısı düştü: {options.authBroken} Veriler bu yüzden boş görünebilir.
          </span>
          <Button size="sm" onClick={reconnect} style={{ background: "#e83e3c" }}>Yeniden Bağlan</Button>
        </div>
      )}

      {anyConnected && (
        <Card className="mb-4"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3 text-sm font-medium"><Settings2 className="w-4 h-4 text-muted-foreground" /> Analiz edilen hesap & property{options?.email ? ` · ${options.email}` : ""}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">GA4 Property</div>
              <Select value={options?.current?.gaPropertyId ?? ""} onValueChange={(v) => setSelection({ gaPropertyId: v })}>
                <SelectTrigger><SelectValue placeholder="GA4 property seç" /></SelectTrigger>
                <SelectContent>{(options?.gaProperties ?? []).map((p) => <SelectItem key={p.propertyId} value={p.propertyId}>{p.displayName || p.propertyId}{p.account ? ` · ${p.account}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">Search Console Property</div>
              <Select value={options?.current?.gscSiteUrl ?? ""} onValueChange={(v) => setSelection({ gscSiteUrl: v })}>
                <SelectTrigger><SelectValue placeholder="GSC site seç" /></SelectTrigger>
                <SelectContent>{(options?.gscSites ?? []).map((s) => <SelectItem key={s.siteUrl} value={s.siteUrl}>{s.siteUrl}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </CardContent></Card>
      )}

      {!anyConnected && (
        <Card className="mb-4" style={{ borderColor: "#e83e3c66", background: "rgba(6,182,212,0.05)" }}>
          <CardContent className="p-5 text-sm text-muted-foreground">
            <div className="font-medium text-foreground mb-1">Henüz hesap bağlı değil</div>
            Google Search Console ve Analytics hesaplarını bağladığında; organik trafik, fırsat motoru, keyword radar, kanibalizasyon, momentum skoru ve AI trafiği burada uçtan uca analiz edilecek.
          </CardContent>
        </Card>
      )}

      {/* Tab geçişi: Trafik Analizi | AI Analizi */}
      <div className="flex gap-2 mb-4">
        {[
          { id: "traffic" as const, label: "Trafik Analizi", icon: BarChart3, c: C.cyan },
          { id: "ai" as const, label: "AI Analizi", icon: Sparkles, c: C.purple },
        ].map((tab) => (
          <button key={tab.id} onClick={() => setView(tab.id)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition"
            style={view === tab.id ? { background: `${tab.c}22`, color: tab.c, border: `1px solid ${tab.c}55` } : { background: "rgba(0,0,0,0.03)", color: "#a3a3a0", border: "1px solid transparent" }}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* Section nav (aktif sekmeye göre) */}
      {anyConnected && (
        <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 mb-5 backdrop-blur bg-white/90 border-b border-black/5 dark:bg-gray-900/90 dark:border-white/10">
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
            {(view === "ai" ? NAV_AI : NAV_TRAFFIC).map((n) => (
              <button key={n.id} onClick={() => scrollTo(n.id)} className="text-xs px-3 py-1.5 rounded-full whitespace-nowrap text-muted-foreground hover:text-foreground transition bg-black/[0.04] dark:bg-white/10">{n.label}</button>
            ))}
          </div>
        </div>
      )}

      {view === "traffic" && (<>
      {/* ════ ÖZET / SEO COPILOT ════ */}
      <Section id="ozet" title="SEO Copilot — Bugün ne oldu?" icon={Zap} desc="Eşik bazlı otomatik uyarılar + momentum skoru" accent={C.amber}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Momentum */}
          <Card style={{ borderColor: mom ? (mom.direction === "up" ? "#12b76a44" : mom.direction === "down" ? "#f0443844" : undefined) : undefined }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2"><Gauge className="w-4 h-4" style={{ color: C.purple }} /><span className="font-semibold text-sm">Traffic Momentum</span></div>
              {mom ? (
                <>
                  <div className="flex items-end gap-2">
                    <span className="text-4xl font-bold tabular-nums" style={{ color: mom.direction === "up" ? C.green : mom.direction === "down" ? C.red : C.amber }}>{mom.score}</span>
                    <span className="text-sm text-muted-foreground mb-1">/100</span>
                    <span className="mb-1.5 ml-auto text-xs px-2 py-0.5 rounded-full" style={{ background: mom.direction === "up" ? "#12b76a22" : mom.direction === "down" ? "#f0443822" : "#f7900922", color: mom.direction === "up" ? C.green : mom.direction === "down" ? C.red : C.amber }}>
                      {mom.direction === "up" ? "Yükseliyor ↑" : mom.direction === "down" ? "Düşüyor ↓" : "Sabit →"}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-black/5 mt-2 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${mom.score}%`, background: mom.direction === "up" ? C.green : mom.direction === "down" ? C.red : C.amber }} /></div>
                  <div className="grid grid-cols-4 gap-1 mt-3 text-center">
                    {[{ l: "Tıklama", v: `${mom.parts.clicks > 0 ? "+" : ""}${mom.parts.clicks}%` }, { l: "Pozisyon", v: `${mom.parts.position > 0 ? "+" : ""}${mom.parts.position}` }, { l: "Gösterim", v: `${mom.parts.impressions > 0 ? "+" : ""}${mom.parts.impressions}%` }, { l: "CTR", v: `${mom.parts.ctr > 0 ? "+" : ""}${mom.parts.ctr}` }].map((p) => (
                      <div key={p.l}><div className="text-xs font-semibold tabular-nums">{p.v}</div><div className="text-[10px] text-muted-foreground">{p.l}</div></div>
                    ))}
                  </div>
                </>
              ) : <div className="text-xs text-muted-foreground py-6 text-center">Veri bekleniyor…</div>}
            </CardContent>
          </Card>
          {/* Alerts */}
          <Card className="lg:col-span-2"><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-4 h-4" style={{ color: C.amber }} /><span className="font-semibold text-sm">Günlük SEO Özeti & Uyarılar</span></div>
            {t.alerts?.length ? (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {t.alerts.map((a, i) => {
                  const conf = a.level === "critical" ? { c: C.red, Icon: AlertTriangle, bg: "#f0443815" } : a.level === "warning" ? { c: C.amber, Icon: AlertTriangle, bg: "#f7900915" } : { c: C.green, Icon: CheckCircle2, bg: "#12b76a15" };
                  return (
                    <div key={i} className="flex items-start gap-2.5 p-2.5 rounded-lg" style={{ background: conf.bg }}>
                      <conf.Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: conf.c }} />
                      <div className="min-w-0"><div className="text-sm font-medium">{a.title}</div><div className="text-xs text-muted-foreground">{a.detail}</div></div>
                    </div>
                  );
                })}
              </div>
            ) : <div className="text-xs text-muted-foreground py-8 text-center">{anyConnected ? "Şu an kritik bir değişiklik yok — her şey normal aralıkta. ✅" : "Bağlantı kurulunca uyarılar burada belirir."}</div>}
          </CardContent></Card>
        </div>
      </Section>

      {/* ════ GENEL BAKIŞ ════ */}
      <Section id="genel" title="Genel Bakış" icon={BarChart3} desc="Son 28 gün · önceki 28 güne göre değişim">
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          {[
            { label: "Toplam Kullanıcı", value: k?.totalUsers, d: k?.dUsers, icon: Users, color: C.blue },
            { label: "Organik Kullanıcı", value: k?.organicUsers, d: k?.dOrganic, icon: Search, color: C.green },
            { label: "Oturum", value: k?.sessions, icon: Activity, color: C.purple },
            { label: "Gösterim", value: k?.impressions, d: k?.dImpressions, icon: Eye, color: C.cyan },
            { label: "Tıklama", value: k?.clicks, d: k?.dClicks, icon: MousePointerClick, color: C.amber },
            { label: "CTR", value: k?.ctr, d: k?.dCtr, dSuffix: "p", icon: Target, color: C.pink, isCtr: true },
            { label: "Ort. Pozisyon", value: k?.avgPosition, d: k?.dPosition, dSuffix: "", icon: TrendingUp, color: C.pink, isPos: true, invert: true },
            { label: "Engagement", value: k?.engagementRate, icon: Activity, color: C.green, isCtr: true },
          ].map((kp) => (
            <Card key={kp.label}><CardContent className="p-4">
              <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground">{kp.label}</span><kp.icon className="w-3.5 h-3.5" style={{ color: kp.color }} /></div>
              <div className="flex items-end justify-between gap-2 mt-1">
                <div className="text-2xl font-bold tabular-nums">{kp.value == null ? "—" : kp.isPos ? kp.value.toFixed(1) : kp.isCtr ? pct(kp.value) : fmt(kp.value)}</div>
                {kp.d != null && <DeltaBadge value={kp.d} invert={kp.invert} suffix={kp.dSuffix ?? "%"} />}
              </div>
            </CardContent></Card>
          ))}
        </div>
      </Section>

      {/* ════ ARAMA PERFORMANSI (GSC arayüzü) + KANALLAR ════ */}
      <Section id="kanallar" title="Arama Performansı & Kanallar" icon={TrendingUp} desc="Search Console arayüzü gibi: çok-metrikli grafik, regex filtre (tüm veriyi filtreler), kanallar">
        {(() => {
          const PM = [
            { key: "clicks", label: "Toplam Tıklama", color: "#4285F4", val: k ? fmt(k.clicks) : "—" },
            { key: "impressions", label: "Toplam Gösterim", color: "#8E44AD", val: k ? fmt(k.impressions) : "—" },
            { key: "ctr", label: "Ortalama TO", color: "#00A36C", val: k ? pct(k.ctr) : "—" },
            { key: "position", label: "Ortalama Konum", color: "#F5A623", val: k ? k.avgPosition.toFixed(1) : "—" },
          ];
          return (
            <Card className="mb-4"><CardContent className="p-4">
              {/* GSC arayüzü gibi çoklu filtre çubuğu */}
              <GscFilterBar domainId={activeDomainId} filters={filters} onChange={setFilters} fetching={isFetching} />
              {/* Metrik kartları (tıkla = grafikte aç/kapa) */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                {PM.map((m) => {
                  const on = metrics.has(m.key);
                  return (
                    <button key={m.key} onClick={() => toggleMetric(m.key)} className="text-left rounded-lg p-3 border transition" style={{ borderColor: on ? m.color : "rgba(0,0,0,0.08)", background: on ? `${m.color}14` : "transparent", opacity: on ? 1 : 0.55 }}>
                      <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color }} /><span className="text-xs text-muted-foreground">{m.label}</span></div>
                      <div className="text-2xl font-bold tabular-nums mt-1" style={{ color: on ? m.color : "#1d1d1b" }}>{m.val}</div>
                    </button>
                  );
                })}
              </div>
              {/* Büyük grafik */}
              <div className="h-80">
                {t.trend?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={t.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#70706c" }} tickMargin={8} minTickGap={24} />
                      <YAxis yAxisId="clicks" hide /><YAxis yAxisId="impressions" hide />
                      <YAxis yAxisId="ctr" hide domain={[0, (max: number) => max * 1.6]} /><YAxis yAxisId="position" hide reversed domain={[1, "dataMax"]} />
                      <RTooltip contentStyle={{ background: "#ffffff", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#1d1d1b" }} formatter={(v: number, n: string) => [n === "Ort. TO" ? pct(v) : n === "Ort. Konum" ? v.toFixed(1) : fmt(v), n]} />
                      {metrics.has("clicks") && <Line yAxisId="clicks" type="monotone" dataKey="clicks" name="Tıklama" stroke="#4285F4" strokeWidth={2} dot={false} />}
                      {metrics.has("impressions") && <Line yAxisId="impressions" type="monotone" dataKey="impressions" name="Gösterim" stroke="#8E44AD" strokeWidth={2} dot={false} />}
                      {metrics.has("ctr") && <Line yAxisId="ctr" type="monotone" dataKey="ctr" name="Ort. TO" stroke="#00A36C" strokeWidth={2} dot={false} />}
                      {metrics.has("position") && <Line yAxisId="position" type="monotone" dataKey="position" name="Ort. Konum" stroke="#F5A623" strokeWidth={2} dot={false} />}
                    </LineChart>
                  </ResponsiveContainer>
                ) : <div className="h-full flex items-center justify-center text-sm text-muted-foreground">{anyConnected ? "Bu aralık/filtre için veri yok." : "Search Console bağlanınca grafik gelecek."}</div>}
              </div>
            </CardContent></Card>
          );
        })()}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Panel title="Kanal Dağılımı" icon={Link2} connected={!!channels.length} color={C.blue}>
            <div className="space-y-2.5">{channels.slice(0, 7).map((c) => <BarRow key={c.source} label={c.source} value={c.users} max={maxCh} color={/organic/i.test(c.source) ? C.green : /direct/i.test(c.source) ? C.blue : /referr/i.test(c.source) ? C.amber : /social/i.test(c.source) ? C.pink : C.purple} suffix={` (%${c.pct})`} />)}</div>
          </Panel>
          <Panel title="Yeni vs Geri Dönen Ziyaretçi" icon={Repeat} connected={!!t.newVsReturning} color={C.purple}>
            {t.newVsReturning && (() => { const nv = t.newVsReturning!; const tot = nv.newUsers + nv.returningUsers || 1; const np = Math.round((nv.newUsers / tot) * 100); return (
              <div>
                <div className="flex h-3 rounded-full overflow-hidden mb-3"><div style={{ width: `${np}%`, background: C.cyan }} /><div style={{ width: `${100 - np}%`, background: C.purple }} /></div>
                <div className="flex justify-between text-sm"><span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: C.cyan }} />Yeni <b className="tabular-nums">{fmt(nv.newUsers)}</b> (%{np})</span><span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: C.purple }} />Geri dönen <b className="tabular-nums">{fmt(nv.returningUsers)}</b></span></div>
              </div>
            ); })()}
          </Panel>
          <Panel title="Etkileşim Kalitesi" icon={Activity} connected={k != null && (k.engagementRate > 0 || k.avgEngagementSec > 0)} color={C.green}>
            <div className="space-y-3 pt-1">
              <Row label="Engagement Rate" value={<b>{k ? pct(k.engagementRate) : "—"}</b>} />
              <Row label="Ort. Oturum Süresi" value={<b>{k ? `${Math.floor(k.avgEngagementSec / 60)}d ${Math.round(k.avgEngagementSec % 60)}s` : "—"}</b>} />
              <Row label="Oturum / Kullanıcı" value={<b>{k && k.totalUsers ? (k.sessions / k.totalUsers).toFixed(2) : "—"}</b>} />
            </div>
          </Panel>
        </div>
      </Section>

      {/* ════ BÖLGELER ════ */}
      <Section id="bolgeler" title="Gelen Bölgeler" icon={MapPin} desc="Ülke ve şehir bazlı ziyaretçi dağılımı" accent={C.green}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Panel title="Ülkeler" icon={Globe} connected={!!t.regions?.length} color={C.green}>
            <div className="space-y-2">{(t.regions ?? []).slice(0, 10).map((r) => <Row key={r.country} label={r.country} value={fmt(r.users)} mono />)}</div>
          </Panel>
          <Panel title="Şehirler" icon={MapPin} connected={!!t.cities?.length} color={C.green}>
            <div className="space-y-2">{(t.cities ?? []).slice(0, 10).map((r) => <Row key={r.city} label={r.city} value={fmt(r.users)} mono />)}</div>
          </Panel>
        </div>
      </Section>

      {/* ════ SAYFALAR ════ */}
      <Section id="sayfalar" title="Sayfalar" icon={FileText} desc="Trafik alan, landing, trafiği düşen ve yeni yükselen sayfalar" accent={C.blue}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel title="En Çok Trafik Alan Sayfalar" icon={TrendingUp} connected={!!t.topPages?.length} color={C.green}>
            <div className="space-y-2">{(t.topPages ?? []).slice(0, 8).map((p) => <Row key={p.page} label={shortUrl(p.page)} value={`${fmt(p.clicks)} tık`} truncate mono href={sayfaLinki(p.page)} />)}</div>
          </Panel>
          <Panel title="Top Landing Pages (giriş sayfaları)" icon={FileText} connected={!!t.landingPages?.length} color={C.blue}>
            <div className="space-y-2">{(t.landingPages ?? []).slice(0, 8).map((p) => <Row key={p.page} label={p.page} value={`${fmt(p.users)} kull.`} truncate mono href={sayfaLinki(p.page)} />)}</div>
          </Panel>
          <Panel title="Trafiği Düşen Sayfalar" icon={TrendingDown} connected={!!t.decliningPages?.length} color={C.red} hint="Önceki döneme göre belirgin düşüş yok. ✅">
            <div className="space-y-2">{(t.decliningPages ?? []).slice(0, 8).map((p) => (
              <div key={p.page} className="flex items-center justify-between gap-2 text-sm"><span className="truncate flex-1 min-w-0" title={p.page}>{shortUrl(p.page)}</span><span className="text-xs flex items-center gap-1.5 shrink-0"><span className="flex items-center gap-1" style={{ color: C.red }}><ArrowDownRight className="w-3 h-3" />%{Math.abs(p.changePct).toFixed(0)}</span>{sayfaLinki(p.page) && <SayfayaGit href={sayfaLinki(p.page)!} />}</span></div>
            ))}</div>
          </Panel>
          <Panel title="Yeni Yükselen Sayfalar" icon={TrendingUp} connected={!!t.risingPages?.length} color={C.green} hint="Yeni yükselen sayfa tespit edilmedi.">
            <div className="space-y-2">{(t.risingPages ?? []).slice(0, 8).map((p) => (
              <div key={p.page} className="flex items-center justify-between gap-2 text-sm"><span className="truncate flex-1 min-w-0" title={p.page}>{shortUrl(p.page)}</span><span className="text-xs flex items-center gap-1.5 shrink-0"><span className="flex items-center gap-1" style={{ color: C.green }}><ArrowUpRight className="w-3 h-3" />{p.previous < 5 ? "yeni" : `%${Math.round(p.changePct)}`}</span>{sayfaLinki(p.page) && <SayfayaGit href={sayfaLinki(p.page)!} />}</span></div>
            ))}</div>
          </Panel>
        </div>
      </Section>

      {/* ════ ANAHTAR KELİMELER ════ */}
      <Section id="kelimeler" title="Anahtar Kelimeler & Sayfalar" icon={Search} desc="Google Search Console gibi: Sorgu/Sayfa, regex filtre, sıralanabilir sütunlar">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Panel title="Brand / Non-Brand" icon={Split} connected={!!brand} color={C.amber} hint="Marka terimi tespit edilemedi.">
            {brand && (
              <div>
                <div className="flex h-3 rounded-full overflow-hidden mb-3"><div style={{ width: `${brandTotal ? (brand.brandClicks / brandTotal) * 100 : 0}%`, background: C.amber }} /><div style={{ flex: 1, background: C.blue }} /></div>
                <div className="space-y-1.5 text-sm">
                  <Row label={<span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: C.amber }} />Brand <span className="text-xs text-muted-foreground">({brand.term})</span></span>} value={`${fmt(brand.brandClicks)} tık`} mono />
                  <Row label={<span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: C.blue }} />Non-Brand</span>} value={`${fmt(brand.nonBrandClicks)} tık`} mono />
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">Marka terimi domain adından türetildi; istenirse özelleştirilebilir.</p>
              </div>
            )}
          </Panel>
          <div className="lg:col-span-2">
            <Card><CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-3">
                {([{ k: "query", l: "Sorgular" }, { k: "page", l: "Sayfalar" }] as const).map((o) => (
                  <button key={o.k} onClick={() => setKwView(o.k)} className="text-xs px-3 py-1.5 rounded-lg font-medium transition" style={kwView === o.k ? { background: `${C.cyan}22`, color: C.cyan } : { background: "rgba(0,0,0,0.04)", color: "#a3a3a0" }}>{o.l}</button>
                ))}
              </div>
              {kwView === "query"
                ? (t.queries?.length
                    ? <GscTable key="q" rows={t.queries.map((q) => ({ label: q.query, clicks: q.clicks, impressions: q.impressions, ctr: q.ctr, position: q.position }))} labelHeader="Sorgu" />
                    : <div className="text-xs text-muted-foreground py-8 text-center">Bağlantı kurulunca sorgular gelecek.</div>)
                : (t.topPages?.length
                    ? <GscTable key="p" rows={t.topPages.map((p) => ({ label: shortUrl(p.page), clicks: p.clicks, impressions: p.impressions, ctr: p.ctr, position: p.position }))} labelHeader="Sayfa" />
                    : <div className="text-xs text-muted-foreground py-8 text-center">Bağlantı kurulunca sayfalar gelecek.</div>)}
            </CardContent></Card>
          </div>
        </div>
      </Section>

      {/* ════ FIRSAT MOTORU ════ */}
      <Section id="firsat" title="Fırsat Motoru" icon={Target} desc="İlk sayfa sınırı, CTR fırsatı ve gösterim madeni" accent={C.green}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Panel title="First Page Push (poz 8-20)" icon={TrendingUp} connected={!!t.opportunities?.firstPagePush?.length} color={C.green} hint="İlk sayfa sınırında sorgu yok.">
            <div className="space-y-2">{(t.opportunities?.firstPagePush ?? []).map((o) => (
              <div key={o.query} className="flex items-center justify-between gap-2 text-sm"><span className="truncate flex-1 min-w-0" title={o.query}>{o.query}</span><span className="text-xs shrink-0 tabular-nums px-1.5 py-0.5 rounded" style={{ background: "#12b76a22", color: C.green }}>poz {o.position.toFixed(1)} · {fmt(o.impressions)} gst</span></div>
            ))}</div>
          </Panel>
          <Panel title="CTR Fırsatı" icon={Target} connected={!!t.opportunities?.ctrOpportunity?.length} color={C.amber} hint="CTR'si beklenenin çok altında sorgu yok.">
            <div className="space-y-2">{(t.opportunities?.ctrOpportunity ?? []).map((o) => (
              <div key={o.query} className="text-sm"><div className="truncate" title={o.query}>{o.query}</div><div className="text-[11px] text-muted-foreground flex gap-2"><span>poz {o.position.toFixed(1)}</span><span>CTR {pct(o.ctr)} → bekl. {pct(o.expectedCtr)}</span><span style={{ color: C.green }}>+{o.potentialClicks} tık</span></div></div>
            ))}</div>
          </Panel>
          <Panel title="Impression Goldmine" icon={Eye} connected={!!t.opportunities?.impressionGoldmine?.length} color={C.cyan} hint="Yüksek gösterim / düşük CTR sorgusu yok.">
            <div className="space-y-2">{(t.opportunities?.impressionGoldmine ?? []).map((o) => (
              <div key={o.query} className="flex items-center justify-between gap-2 text-sm"><span className="truncate flex-1 min-w-0" title={o.query}>{o.query}</span><span className="text-xs shrink-0 tabular-nums">{fmt(o.impressions)} gst · {pct(o.ctr)}</span></div>
            ))}</div>
          </Panel>
        </div>
      </Section>

      {/* ════ KEYWORD RADAR ════ */}
      <Section id="radar" title="Keyword Movement Radar" icon={Radar} desc="Önceki döneme göre yükselen, düşen, yeni kazanılan ve kaybedilen sıralamalar" accent={C.pink}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <Panel title="⬆️ Hızlı Yükselenler" icon={TrendingUp} connected={!!t.keywordRadar?.rising?.length} color={C.green} hint="—">
            <div className="space-y-1.5">{(t.keywordRadar?.rising ?? []).map((r) => <div key={r.query} className="text-sm flex items-center justify-between gap-2"><span className="truncate flex-1 min-w-0" title={r.query}>{r.query}</span><span className="text-xs shrink-0 tabular-nums" style={{ color: C.green }}>{r.prev.toFixed(0)}→{r.cur.toFixed(0)}</span></div>)}</div>
          </Panel>
          <Panel title="⬇️ Hızlı Düşenler" icon={TrendingDown} connected={!!t.keywordRadar?.falling?.length} color={C.red} hint="—">
            <div className="space-y-1.5">{(t.keywordRadar?.falling ?? []).map((r) => <div key={r.query} className="text-sm flex items-center justify-between gap-2"><span className="truncate flex-1 min-w-0" title={r.query}>{r.query}</span><span className="text-xs shrink-0 tabular-nums" style={{ color: C.red }}>{r.prev.toFixed(0)}→{r.cur.toFixed(0)}</span></div>)}</div>
          </Panel>
          <Panel title="✨ Yeni Kazanılanlar (ilk 10)" icon={Trophy} connected={!!t.keywordRadar?.gained?.length} color={C.cyan} hint="—">
            <div className="space-y-1.5">{(t.keywordRadar?.gained ?? []).map((r) => <div key={r.query} className="text-sm flex items-center justify-between gap-2"><span className="truncate flex-1 min-w-0" title={r.query}>{r.query}</span><span className="text-xs shrink-0 tabular-nums" style={{ color: C.cyan }}>#{r.cur.toFixed(0)}</span></div>)}</div>
          </Panel>
          <Panel title="💤 Kaybedilenler (ilk 10'dan)" icon={ArrowDownRight} connected={!!t.keywordRadar?.lost?.length} color={C.amber} hint="—">
            <div className="space-y-1.5">{(t.keywordRadar?.lost ?? []).map((r) => <div key={r.query} className="text-sm flex items-center justify-between gap-2"><span className="truncate flex-1 min-w-0" title={r.query}>{r.query}</span><span className="text-xs shrink-0 tabular-nums" style={{ color: C.amber }}>#{r.prev.toFixed(0)}→</span></div>)}</div>
          </Panel>
        </div>
      </Section>

      {/* ════ KANİBALİZASYON ════ */}
      <Section id="kanibal" title="Cannibalization Detector" icon={Split} desc="Aynı anahtar kelime için birden fazla sayfanın yarıştığı durumlar" accent={C.red}>
        <Panel title="Kanibalizasyon tespit edilen sorgular" icon={Split} connected={!!t.cannibalization?.length} color={C.red} hint="Kanibalizasyon tespit edilmedi — her sorgu için tek baskın sayfa var. ✅">
          <div className="space-y-3">{(t.cannibalization ?? []).map((c) => (
            <div key={c.query} className="p-3 rounded-lg" style={{ background: "rgba(239,68,68,0.06)" }}>
              <div className="text-sm font-medium mb-1.5">"{c.query}" <span className="text-xs text-muted-foreground">— {c.pages.length} sayfa yarışıyor</span></div>
              <div className="space-y-1">{c.pages.slice(0, 4).map((p) => <div key={p.page} className="flex items-center justify-between gap-2 text-xs"><span className="truncate flex-1 min-w-0" title={p.page}>{shortUrl(p.page)}</span><span className="shrink-0 tabular-nums text-muted-foreground">poz {p.position.toFixed(1)} · {fmt(p.clicks)} tık</span></div>)}</div>
            </div>
          ))}</div>
        </Panel>
      </Section>

      </>)}

      {view === "ai" && (<>
      {/* ════ AI TRAFİĞİ ════ */}
      {/* ════ AI GELİŞİM KARNESİ (GEO) — SEO karne taslağının AI karşılığı ════ */}
      <Section id="ai-growth" title="AI Gelişim Karnesi" icon={Sparkles} desc="Başlangıçtan bugüne GEO performansı: AI trafiği, AI Overview görünürlüğü ve motor kapsaması nasıl değişti" accent="#771e1d">
        {(() => {
          const g = aiGrowth;
          if (!g?.hasData) return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Karne için geçmiş veri gerekiyor — AI Overview analizi ve AI Visibility koşuları biriktikçe burası dolar (ilk koşular zaten "Başlangıç" satırını oluşturur).</CardContent></Card>;
          const R = "#771e1d"; // taslaktaki kırmızı vurgu
          const dt = (s: string | null) => (s ? new Date(s).toLocaleDateString("tr-TR", { month: "short", year: "numeric" }) : "—");
          const rows = [
            { l: "Aylık AI Trafiği", a: g.start.aiUsersMonthly, b: g.now.aiUsersMonthly, suf: " kullanıcı" },
            { l: "AI Overview'de Sıralanan Soru", a: g.start.placements, b: g.now.placements, suf: "" },
            { l: "AI Görünürlük Skoru", a: g.start.visibilityScore, b: g.now.visibilityScore, suf: "/100" },
            { l: "Görünür Olduğumuz AI Motoru", a: g.start.enginesVisible, b: g.now.enginesVisible, suf: "" },
            { l: "AI Anılma Payı", a: g.start.share, b: g.now.share, suf: "%" },
          ];
          return (<>
            {/* Başlangıç → SONUÇ paneli */}
            <Card className="mb-4"><CardContent className="p-5">
              <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center">
                <div>
                  <div className="text-center font-bold text-base mb-3">Başlangıç <span className="text-[11px] font-normal text-muted-foreground">({dt(g.start.date)})</span></div>
                  {rows.map((r) => (
                    <div key={r.l} className="flex items-center justify-between border-b py-2 text-sm" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                      <span className="text-muted-foreground">{r.l}</span><b className="tabular-nums">{r.a.toLocaleString("tr-TR")}{r.suf}</b>
                    </div>
                  ))}
                </div>
                <div className="text-4xl font-bold" style={{ color: R }}>→</div>
                <div>
                  <div className="text-center font-bold text-base mb-3">SONUÇ <span className="text-[11px] font-normal text-muted-foreground">({dt(g.now.date)})</span></div>
                  {rows.map((r) => (
                    <div key={r.l} className="flex items-center justify-between border-b py-2 text-sm" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                      <span className="text-muted-foreground">{r.l}</span>
                      <b className="tabular-nums" style={{ color: r.b > r.a ? "#039855" : r.b < r.a ? "#d92d20" : undefined }}>{r.b.toLocaleString("tr-TR")}{r.suf}</b>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent></Card>

            {/* Büyük vurgular — taslaktaki kırmızı istatistikler */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {[
                { v: `+${g.highlights.newQuestions}`, l: "Yeni AI Sorusunda Görünürlük" },
                { v: g.highlights.trafficMultiplier ? `${g.highlights.trafficMultiplier}x` : (g.now.aiUsersMonthly > 0 ? "YENİ" : "—"), l: "AI Trafiğinde Büyüme" },
                { v: g.highlights.trafficGrowthPct !== null ? `%${g.highlights.trafficGrowthPct}` : (g.highlights.placementGrowthPct !== null ? `%${g.highlights.placementGrowthPct}` : "—"), l: g.highlights.trafficGrowthPct !== null ? "AI Trafiğinde Büyüme Oranı" : "AI Görünürlüğünde Büyüme Oranı" },
              ].map((s, i) => (
                <Card key={i}><CardContent className="p-6 text-center">
                  <div className="text-4xl font-extrabold tabular-nums" style={{ color: R }}>{s.v}</div>
                  <div className="text-[11px] text-muted-foreground mt-1.5">{s.l}</div>
                </CardContent></Card>
              ))}
            </div>

            {/* Grafikler: aylık AI trafiği + görünürlük trendi */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
              <Card><CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-semibold">AI Trafiği Gelişimi <span className="text-[11px] font-normal text-muted-foreground">· GA4</span></div>
                  <div className="flex rounded-lg border overflow-hidden text-[11px]">
                    {([["yearly", "Yıllık"], ["monthly", "Aylık"]] as const).map(([tf, lbl]) => (
                      <button key={tf} onClick={() => setAiGrTf(tf)}
                        className="px-2.5 py-1 font-medium transition-colors"
                        style={aiGrTf === tf ? { background: R, color: "#fff" } : { color: "#70706c" }}>{lbl}</button>
                    ))}
                  </div>
                </div>
                {aiGrTf === "yearly" && g.yoy && (
                  <div className="text-[11px] text-muted-foreground mb-1">
                    {g.yoy.prevYear}: <b className="tabular-nums">{g.yoy.prevUsers.toLocaleString("tr-TR")}</b> → {g.yoy.curYear}{g.yoy.curPartial ? " (devam ediyor)" : ""}: <b className="tabular-nums" style={{ color: R }}>{g.yoy.curUsers.toLocaleString("tr-TR")}</b>
                    {g.yoy.pct !== null && <span className="ml-1.5 font-bold" style={{ color: g.yoy.pct >= 0 ? "#16a34a" : "#d92d20" }}>{g.yoy.pct >= 0 ? "+" : ""}%{g.yoy.pct}</span>}
                  </div>
                )}
                <div className="h-[220px]">
                  {aiGrTf === "yearly" ? (
                    (g.yearlyAiUsers?.length ?? 0) > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={g.yearlyAiUsers} margin={{ top: 22, right: 8, left: -18, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                          <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#a3a3a0" }} tickFormatter={(y: string, i: number) => (g.yearlyAiUsers?.[i]?.partial ? `${y}*` : y)} />
                          <YAxis tick={{ fontSize: 10, fill: "#a3a3a0" }} allowDecimals={false} />
                          <RTooltip contentStyle={{ fontSize: 12, borderRadius: 10 }} formatter={(v: number) => [v.toLocaleString("tr-TR"), "AI kullanıcı (yıl toplamı)"]} />
                          <Bar dataKey="users" fill={R} radius={[6, 6, 0, 0]} maxBarSize={72}>
                            <LabelList dataKey="users" position="top" style={{ fontSize: 11, fontWeight: 700, fill: R }} formatter={(v: number) => v.toLocaleString("tr-TR")} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <div className="h-full flex items-center justify-center text-xs text-muted-foreground">GA4 bağlı değil veya AI kaynak verisi yok.</div>
                  ) : g.monthlyAiUsers.length > 1 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={g.monthlyAiUsers} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
                        <defs><linearGradient id="aiGrG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={R} stopOpacity={0.3} /><stop offset="100%" stopColor={R} stopOpacity={0.02} /></linearGradient></defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis dataKey="month" tick={{ fontSize: 9, fill: "#a3a3a0" }} tickFormatter={(m: string) => m.slice(2)} />
                        <YAxis tick={{ fontSize: 10, fill: "#a3a3a0" }} allowDecimals={false} />
                        <RTooltip contentStyle={{ fontSize: 12, borderRadius: 10 }} formatter={(v: number) => [v, "AI kullanıcı"]} />
                        <Area type="monotone" dataKey="users" stroke={R} strokeWidth={2.5} fill="url(#aiGrG)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : <div className="h-full flex items-center justify-center text-xs text-muted-foreground">GA4 bağlı değil veya AI kaynak verisi yok.</div>}
                </div>
                {aiGrTf === "yearly" && (g.yearlyAiUsers?.some((y) => y.partial) ?? false) && (
                  <div className="text-[10px] text-muted-foreground mt-1">* Cari yıl henüz tamamlanmadı — toplam bugüne kadarki veridir.</div>
                )}
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <div className="text-sm font-semibold mb-1">AI Görünürlük Gelişimi <span className="text-[11px] font-normal text-muted-foreground">· koşu bazlı (skor + sıralanan soru)</span></div>
                <div className="h-[220px]">
                  {(g.scoreTrend.length > 1 || g.placementTrend.length > 1) ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis dataKey="d" type="category" allowDuplicatedCategory={false} tick={{ fontSize: 9, fill: "#a3a3a0" }} />
                        <YAxis tick={{ fontSize: 10, fill: "#a3a3a0" }} allowDecimals={false} />
                        <RTooltip contentStyle={{ fontSize: 12, borderRadius: 10 }} />
                        <Line data={g.scoreTrend.map((t) => ({ d: new Date(t.date).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }), v: t.score }))} dataKey="v" name="Görünürlük Skoru" type="monotone" stroke={C.purple} strokeWidth={2} dot={{ r: 2.5 }} />
                        <Line data={g.placementTrend.map((t) => ({ d: new Date(t.date).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }), v: t.placements }))} dataKey="v" name="Sıralanan Soru" type="monotone" stroke={R} strokeWidth={2} dot={{ r: 2.5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <div className="h-full flex items-center justify-center text-xs text-muted-foreground">Trend için en az 2 koşu gerekli — analizler biriktikçe dolar.</div>}
                </div>
              </CardContent></Card>
            </div>

            {/* AI Başarı Tablomuz — taslaktaki "SEO Başarı tablomuz" düzeni */}
            {g.successTable.length > 0 && (
              <Card className="mb-4"><CardContent className="p-5">
                <div className="text-base font-bold mb-1">AI Başarı Tablomuz</div>
                <p className="text-xs text-muted-foreground mb-4">AI Overview'de elde ettiğimiz gerçek görünürlükler — soru, AI arama hacmi ve kaynak sıramız.</p>
                <div className="grid grid-cols-[minmax(180px,2fr)_auto_minmax(160px,3fr)_auto] gap-x-4 gap-y-2.5 items-center text-xs">
                  <div className="font-semibold" style={{ color: R }}>AI Sorusu</div>
                  <div className="font-semibold" style={{ color: R }}>AI Hacmi</div>
                  <div />
                  <div className="font-semibold text-right" style={{ color: R }}>Pozisyon</div>
                  {g.successTable.map((s) => (
                    <FragmentRow key={s.question} s={s} R={R} />
                  ))}
                </div>
              </CardContent></Card>
            )}
          </>);
        })()}
      </Section>

      <Section id="ai-traffic" title="AI'dan Gelen Gerçek Trafik" icon={Sparkles} desc="ChatGPT/Perplexity/Gemini/Claude/Copilot'tan siteye gerçekten gelen ziyaretçiler — GA4 referral (ölçülmüş veri, sentetik değil)" accent={C.purple}>
        {(() => {
          const ENGINE_C: Record<string, string> = { ChatGPT: "#10a37f", Perplexity: "#20808d", Gemini: "#4285F4", Claude: "#d97757", Copilot: "#0078d4", "You.com": "#6b46c1" };
          const sum = t.aiSummary, byEng = t.aiByEngine ?? [], landing = t.aiLandingPages ?? [];
          const hasAi = (sum?.totalUsers ?? 0) > 0 || byEng.length > 0;
          if (!t.ga?.connected) return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground"><Sparkles className="w-8 h-8 mx-auto mb-2 opacity-40" />GA4 bağlı değil — gerçek AI trafiğini görmek için Google Analytics'i bağla.</CardContent></Card>;
          if (!hasAi) return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground"><Sparkles className="w-8 h-8 mx-auto mb-2 opacity-40" />Seçili dönemde AI motorlarından ölçülmüş trafik yok. (AI'lar her zaman tıklanabilir link vermez; görünürlük için aşağıdaki Google AI Overview + Model Bazlı Görünürlük'e bak.)</CardContent></Card>;
          return (<>
            {/* Özet KPI */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[
                { label: "AI'dan Toplam Ziyaretçi", value: fmt(sum?.totalUsers ?? 0), sub: sum && sum.prevUsers >= 0 ? `${sum.changePct >= 0 ? "↑" : "↓"} %${Math.abs(sum.changePct)} (önceki döneme göre)` : "", color: C.purple },
                { label: "Oturum", value: fmt(sum?.totalSessions ?? 0), sub: "", color: C.cyan },
                { label: "Dönüşüm (key events)", value: fmt(sum?.totalConversions ?? 0), sub: "", color: C.green },
                { label: "Aktif AI Motoru", value: String(byEng.length), sub: byEng.map((e) => e.engine).slice(0, 3).join(", "), color: C.amber },
              ].map((s) => (
                <Card key={s.label}><CardContent className="p-4">
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                  <div className="text-2xl font-bold mt-0.5 tabular-nums" style={{ color: s.color }}>{s.value}</div>
                  {s.sub && <div className="text-[11px] text-muted-foreground mt-0.5">{s.sub}</div>}
                </CardContent></Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Motor bazında + trend */}
              <div className="lg:col-span-2 space-y-4">
                <Card><CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4" style={{ color: C.purple }} /><span className="font-semibold text-sm">Motor Bazında Gerçek Trafik</span></div>
                  <div className="space-y-2.5">{byEng.map((e) => {
                    const ec = ENGINE_C[e.engine] ?? C.purple;
                    const max = Math.max(...byEng.map((x) => x.users), 1);
                    return (
                      <div key={e.engine}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="font-medium inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: ec }} />{e.engine}</span>
                          <span className="text-muted-foreground tabular-nums">{fmt(e.users)} ziyaretçi · {fmt(e.sessions)} oturum{e.conversions > 0 ? ` · ${fmt(e.conversions)} dönüşüm` : ""}</span>
                        </div>
                        <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(0,0,0,0.05)" }}><div className="h-full rounded-full" style={{ width: `${(e.users / max) * 100}%`, background: ec }} /></div>
                      </div>
                    );
                  })}</div>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: C.purple }} /><span className="font-semibold text-sm">AI Trafik Trendi</span><span className="text-[11px] text-muted-foreground">— motor bazında (tıkla aç/kapa)</span></div>
                    {/* Motor toggle çipleri (GSC grafiği gibi) */}
                    <div className="flex flex-wrap gap-1.5">
                      {byEng.map((e) => {
                        const ec = ENGINE_C[e.engine] ?? C.purple, on = aiEng.has(e.engine);
                        return (
                          <button key={e.engine} onClick={() => toggleAiEng(e.engine)} className="text-xs px-2.5 py-1 rounded-full border transition inline-flex items-center gap-1.5" style={{ borderColor: on ? ec : "rgba(0,0,0,0.12)", background: on ? `${ec}1a` : "transparent", color: on ? ec : "#70706c", opacity: on ? 1 : 0.6 }}>
                            <span className="w-2 h-2 rounded-full" style={{ background: ec }} />{e.engine} <span className="tabular-nums opacity-80">{fmt(e.users)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="h-80">
                    {t.aiTrend?.length ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={t.aiTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#70706c" }} tickMargin={8} minTickGap={24} />
                          <YAxis tick={{ fontSize: 11, fill: "#70706c" }} allowDecimals={false} width={32} />
                          <RTooltip contentStyle={{ background: "#ffffff", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#1d1d1b" }} />
                          {byEng.map((e) => aiEng.has(e.engine) && (
                            <Line key={e.engine} type="monotone" dataKey={e.engine} name={e.engine} stroke={ENGINE_C[e.engine] ?? C.purple} strokeWidth={2} dot={false} connectNulls />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    ) : <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Bu aralık için AI trafik trendi yok.</div>}
                  </div>
                </CardContent></Card>
              </div>

              {/* AI hangi sayfaya getiriyor */}
              <Panel title="AI Hangi Sayfaya Getiriyor" icon={Sparkles} connected={landing.length > 0} color={C.purple} hint="AI motorlarından gelen ziyaretçilerin indiği sayfalar.">
                <div className="space-y-2">{landing.map((p, i) => {
                  const ec = ENGINE_C[p.engine] ?? C.purple;
                  return (
                    <div key={i} className="text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate flex-1 min-w-0" title={p.page}>{p.page}</span>
                        <span className="tabular-nums shrink-0 text-muted-foreground">{fmt(p.users)}</span>
                      </div>
                      <span className="text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ background: `${ec}1a`, color: ec }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: ec }} />{p.engine}{p.conversions > 0 ? ` · ${fmt(p.conversions)} dönüşüm` : ""}</span>
                    </div>
                  );
                })}</div>
              </Panel>
            </div>

            {/* 🔮 AI PROMPT TAHMİNİ — "bu trafik hangi prompt'lardan geldi?" (3 sinyal çaprazı) */}
            {promptInf?.configured && landing.length > 0 && (() => {
              const normPath = (u: string) => { try { const p = u.startsWith("http") ? new URL(u).pathname : u; return (p.replace(/\/+$/, "").toLowerCase() || "/"); } catch { return u.toLowerCase(); } };
              const platEngine = (plat: string) => {
                const p = (plat || "").toLowerCase().replace(/[^a-z]/g, "");
                if (p.includes("chatgpt") || p.includes("gpt")) return "ChatGPT";
                if (p.includes("perplexity")) return "Perplexity";
                if (p.includes("gemini")) return "Gemini";
                if (p.includes("claude")) return "Claude";
                if (p.includes("copilot") || p.includes("bing")) return "Copilot";
                if (p.includes("google") || p.includes("overview") || p.includes("aimode")) return "Google";
                return plat;
              };
              const byPath = new Map<string, PromptInf["placements"]>();
              for (const pl of promptInf.placements) {
                const k = normPath(pl.url);
                const list = byPath.get(k) ?? []; list.push(pl); byPath.set(k, list);
              }
              return (
                <Card className="mt-4"><CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1"><span className="text-base">🔮</span><span className="font-semibold text-sm">AI Prompt Tahmini</span>
                    <span className="text-[11px] text-muted-foreground">— AI trafiğinin hangi sorulardan geldiğinin kanıta dayalı tahmini (prompt referrer'da taşınmaz; iniş sayfası × AI Overview kaynakları × GSC çaprazlanır)</span></div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                    {landing.slice(0, 8).map((p, i) => {
                      const path = normPath(p.page);
                      const pls = byPath.get(path) ?? [];
                      const strong = pls.filter((x) => platEngine(x.platform) === p.engine).slice(0, 3);
                      const medium = pls.filter((x) => platEngine(x.platform) !== p.engine).slice(0, 3 - Math.min(2, strong.length));
                      const gscQ = (promptInf.gscPageQueries[path] ?? []).slice(0, 3);
                      const ec = ENGINE_C[p.engine] ?? C.purple;
                      return (
                        <div key={i} className="rounded-lg border p-3" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <span className="text-xs font-medium truncate" title={p.page}>{p.page}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded shrink-0" style={{ background: `${ec}1a`, color: ec }}>{p.engine} · {fmt(p.users)} ziyaret</span>
                          </div>
                          <div className="space-y-1">
                            {strong.map((q, j) => (
                              <div key={`s${j}`} className="text-[11px]">
                                <span className="px-1 rounded mr-1" style={{ background: "rgba(34,197,94,0.12)", color: "#039855" }}>yüksek güven</span>
                                “{q.question}” <span className="text-muted-foreground">— {p.engine} cevabında {q.position}. kaynak{q.aiSearchVolume ? ` · ${fmt(q.aiSearchVolume)} hacim` : ""}</span>
                              </div>
                            ))}
                            {medium.map((q, j) => (
                              <div key={`m${j}`} className="text-[11px]">
                                <span className="px-1 rounded mr-1" style={{ background: "rgba(245,158,11,0.12)", color: "#b54708" }}>orta</span>
                                “{q.question}” <span className="text-muted-foreground">— {platEngine(q.platform)} kaynağı, poz {q.position}</span>
                              </div>
                            ))}
                            {gscQ.map((q, j) => (
                              <div key={`g${j}`} className="text-[11px]">
                                <span className="px-1 rounded mr-1" style={{ background: "rgba(59,130,246,0.12)", color: "#b12220" }}>GSC</span>
                                “{q.query}” <span className="text-muted-foreground">— {fmt(q.clicks)} tık (Google/AI Overview sinyali)</span>
                              </div>
                            ))}
                            {strong.length === 0 && medium.length === 0 && gscQ.length === 0 && (
                              <div className="text-[11px] text-muted-foreground">Bu sayfa için eşleşen soru yok — AI Overview analizini yeniden çalıştırmak eşleşme şansını artırır.</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {promptInf.ovRunAt && <div className="text-[10px] text-muted-foreground mt-2">AI Overview kaynak verisi: {new Date(promptInf.ovRunAt).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" })} çalıştırması · GSC: son 90 gün</div>}
                </CardContent></Card>
              );
            })()}
          </>);
        })()}
      </Section>

      {/* ════ GOOGLE AI OVERVIEW (DataForSEO llm_mentions) ════ */}
      <Section id="ai-overview" title="Google AI Overview" icon={Search} desc="Google aramada çıkan AI Overview'larda kim görünüyor — gerçek veri (DataForSEO)" accent={C.cyan}>
        <Card><CardContent className="p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
            <div className="text-sm text-muted-foreground">
              {ov?.latest
                ? <>Son analiz: <b className="text-foreground">{new Date(ov.latest.runAt).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" })}</b> · {ov.latest.totalQuestions} gerçek soru · {ov.latest.meta.topicCount} konu · domain payın: <b style={{ color: C.cyan }}>%{ov.latest.share}</b></>
                : "Google AI Overview'larda hangi gerçek sorularda kimin çıktığını ve senin domainin görünüp görünmediğini gösterir (DataForSEO LLM Mentions — gerçek veri)."}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex items-center rounded-lg border p-0.5" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
                {([{ k: "auto", l: "Oto" }, { k: "tr", l: "🇹🇷 TR" }, { k: "en", l: "🌍 EN" }, { k: "ar", l: "🇸🇦 AR" }] as const).map((o) => (
                  <button key={o.k} onClick={() => setAiMarket(o.k)} title="AI Overview'in pazarı/lokasyonu" className="text-xs px-2 py-1 rounded-md transition" style={aiMarket === o.k ? { background: `${C.cyan}33`, color: "#b12220" } : { color: "#70706c" }}>{o.l}</button>
                ))}
              </div>
              <Button size="sm" disabled={!ov?.configured || runOv.isPending || ov?.running} onClick={async () => { if (await confirmCost("ai-overview")) runOv.mutate(); }} className="gap-1.5" style={{ background: C.cyan, color: "#141412" }}>
                {runOv.isPending || ov?.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                {runOv.isPending || ov?.running ? "Analiz ediliyor… (~4 dk)" : ov?.latest ? "Yeniden Derin Analiz (~500 token)" : "Derin Analiz Et (~500 token)"}
              </Button>
            </div>
          </div>
          {!ov?.configured && <p className="text-xs" style={{ color: C.amber }}>DataForSEO kimlik bilgileri tanımlı değil.</p>}
          {runOv.isError && <p className="text-xs text-red-400">{(runOv.error as Error)?.message}</p>}
          {ov?.runError && <p className="text-xs text-red-400">{ov.runError}</p>}
          {ov?.latest && <p className="text-[11px] text-muted-foreground mt-1">Bu veri Google aramada çıkan <b className="text-foreground">AI Overview</b> kutularından toplanır (platform: google).</p>}
        </CardContent></Card>

        {(runOv.isPending || ov?.running) && <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-6"><Loader2 className="w-4 h-4 animate-spin" /> Analiz arka planda sürüyor — sıralandığımız tüm sorular 4 AI motorunda canlı test ediliyor (~4 dk). Sayfadan ayrılabilirsin, bitince otomatik yenilenir.</div>}

        {ov?.latest && (<>
          <div className="mt-4">
            <Panel title={`AI Overview'lerde Sıralandığımız Yerler (${ov.latest.placementsTotal})`} icon={Trophy} connected={!!ov.latest.placements.length} color={C.green} hint="Bu domain henüz hiçbir Google AI Overview'de kaynak gösterilmemiş.">
              <div className="flex items-center gap-1.5 mb-2 text-xs">
                <span className="text-muted-foreground">Sırala:</span>
                {([{ k: "volume", l: "Hacme göre" }, { k: "position", l: "Konuma göre" }] as const).map((o) => (
                  <button key={o.k} onClick={() => setPlaceSort(o.k)} className="px-2.5 py-0.5 rounded-full transition" style={placeSort === o.k ? { background: `${C.green}22`, color: C.green } : { background: "rgba(0,0,0,0.05)", color: "#a3a3a0" }}>{o.l}</button>
                ))}
              </div>
              <div className="space-y-1.5 max-h-[440px] overflow-y-auto pr-1">{[...ov.latest.placements].sort((a, b) => placeSort === "position" ? (a.position || 99) - (b.position || 99) : b.aiSearchVolume - a.aiSearchVolume).map((p, i) => (
                <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm border-b last:border-0 pb-1.5 hover:bg-black/[0.03] rounded px-1 transition">
                  <span className="text-[11px] font-bold tabular-nums px-2 py-1 rounded shrink-0" style={{ background: p.position <= 3 ? `${C.green}22` : "rgba(0,0,0,0.05)", color: p.position <= 3 ? C.green : "#a3a3a0" }}>#{p.position}/{p.totalSources}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{p.question}</div>
                    <div className="truncate text-[11px]" style={{ color: C.cyan }}>{shortUrl((p.url || "").split("#:~:")[0])}</div>
                  </div>
                  <span className="text-xs text-muted-foreground tabular-nums shrink-0" title="AI arama hacmi">{fmt(p.aiSearchVolume)}</span>
                </a>
              ))}</div>
            </Panel>
          </div>

          {!!ov.latest.engineMatrix.length && (() => {
            // Görünür olduğumuz motorların sayısına göre sırala — GPT/Gemini/Claude/Perplexity'de
            // gerçekten çıktığımız sorular en üstte dursun, tablo yanıltmasın.
            const mScore = (m: OvEngine) => [m.chatgpt, m.gemini, m.claude, m.perplexity].filter(Boolean).length * 2 + (!m.chatgpt && m.chatgptSource ? 1 : 0);
            const rows = [...ov.latest!.engineMatrix].sort((a, b) => (mScore(b) - mScore(a)) || b.aiSearchVolume - a.aiSearchVolume);
            const cnt = {
              chatgpt: rows.filter((m) => m.chatgpt).length, chatgptSrc: rows.filter((m) => !m.chatgpt && m.chatgptSource).length,
              gemini: rows.filter((m) => m.gemini).length,
              claude: rows.filter((m) => m.claude).length, perplexity: rows.filter((m) => m.perplexity).length,
            };
            return (
            <div className="mt-4">
              <Panel title={`AI Motorlarında Görünürlük — sıralandığımız ${rows.length} sorunun tamamı`} icon={Sparkles} connected color={C.purple}>
                <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
                  <span className="px-2 py-0.5 rounded-full" style={cnt.chatgpt + cnt.chatgptSrc > 0 ? { background: `${C.green}22`, color: C.green } : { background: "rgba(0,0,0,0.05)", color: "#a3a3a0" }}>ChatGPT: {cnt.chatgpt} cevapta · {cnt.chatgptSrc} kaynaklarda</span>
                  {([["Gemini", cnt.gemini], ["Claude", cnt.claude], ["Perplexity", cnt.perplexity]] as const).map(([l, n]) => (
                    <span key={l} className="px-2 py-0.5 rounded-full" style={n > 0 ? { background: `${C.green}22`, color: C.green } : { background: "rgba(0,0,0,0.05)", color: "#a3a3a0" }}>{l}: {n}/{rows.length}</span>
                  ))}
                </div>
                <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                  <table className="w-full text-sm min-w-[620px]">
                    <thead className="sticky top-0 bg-background"><tr className="text-xs text-muted-foreground text-left border-b">
                      <th className="py-2">Soru</th><th className="py-2 text-right">Hacim</th>
                      {["Google AIO", "ChatGPT", "Gemini", "Claude", "Perplexity"].map((h) => <th key={h} className="py-2 px-2 text-center whitespace-nowrap">{h}</th>)}
                    </tr></thead>
                    <tbody>{rows.map((m, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2 pr-3">{m.question}</td>
                        <td className="py-2 text-right text-muted-foreground tabular-nums">{fmt(m.aiSearchVolume)}</td>
                        <td className="py-2 px-2 text-center">{m.google ? <Check className="w-4 h-4 inline" style={{ color: C.green }} /> : <span className="text-muted-foreground">·</span>}</td>
                        <td className="py-2 px-2 text-center" title={m.chatgpt ? "ChatGPT cevabında anıldık / atıf aldık" : m.chatgptSource ? "ChatGPT'nin Kaynaklar panelinde varız (cevapta anılmadık)" : "ChatGPT'de görünmüyoruz"}>
                          {m.chatgpt ? <Check className="w-4 h-4 inline" style={{ color: C.green }} />
                            : m.chatgptSource ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${C.amber}22`, color: C.amber }}>K</span>
                            : <span className="text-muted-foreground">·</span>}
                        </td>
                        {[m.gemini, m.claude, m.perplexity].map((v, j) => (
                          <td key={j} className="py-2 px-2 text-center">{v ? <Check className="w-4 h-4 inline" style={{ color: C.green }} /> : <span className="text-muted-foreground">·</span>}</td>
                        ))}
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2"><b>Google AIO</b> = AI Overview'de kaynak gösterildik. <b>ChatGPT</b> = gerçek ChatGPT araması: <Check className="w-3 h-3 inline" style={{ color: C.green }} /> cevapta anıldık/atıf aldık, <span className="text-[10px] font-bold px-1 rounded" style={{ background: `${C.amber}22`, color: C.amber }}>K</span> = cevapta anılmadık ama <b>Kaynaklar panelinde</b> varız (kullanıcı kaynağa tıklayabilir). Gemini/Claude/Perplexity = canlı sorulduğunda markamız cevapta anılıyor mu. (· = fırsat: o motorda görünmüyoruz. Cevaplar anlıktır — sınırda olduğumuz sorular çalıştırmadan çalıştırmaya değişebilir.)</p>
              </Panel>
            </div>
            );
          })()}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <Panel title="AI Overview'da Hâkim Domainler" icon={Trophy} connected={!!ov.latest.topDomains.length} color={C.cyan} hint="—">
              {(() => {
                const max = Math.max(...ov.latest!.topDomains.map((d) => d.mentions), 1);
                return (
                  <div className="space-y-2.5">{ov.latest!.topDomains.slice(0, 12).map((d, i) => (
                    <div key={d.domain} className="space-y-1">
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="truncate flex-1 min-w-0 flex items-center gap-1.5"><span className="text-muted-foreground">{i + 1}.</span> {d.domain}{d.isUs && <span className="text-[10px] px-1.5 py-0.5 rounded shrink-0" style={{ background: `${C.green}22`, color: C.green }}>BİZ</span>}</span>
                        <span className="text-muted-foreground tabular-nums shrink-0">{d.mentions}×</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-black/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(d.mentions / max) * 100}%`, background: d.isUs ? C.green : C.cyan }} /></div>
                    </div>
                  ))}</div>
                );
              })()}
            </Panel>
            <Panel title="Gerçek AI Soruları (arama hacmiyle)" icon={Search} connected={!!ov.latest.questions.length} color={C.cyan} hint="—">
              <div className="space-y-1.5 max-h-[360px] overflow-y-auto pr-1">{ov.latest.questions.map((q, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-sm border-b last:border-0 pb-1.5">
                  <span className="truncate flex-1 min-w-0 flex items-center gap-1.5">{q.cited && <span className="text-[10px] px-1.5 py-0.5 rounded shrink-0" style={{ background: `${C.green}22`, color: C.green }}>✓BİZ</span>}{q.question}</span>
                  <span className="text-xs text-muted-foreground tabular-nums shrink-0" title="AI arama hacmi">{fmt(q.aiSearchVolume)}</span>
                </div>
              ))}</div>
            </Panel>
          </div>
        </>)}
      </Section>

      {/* ════ AI VISIBILITY (DataForSEO) ════ */}
      <Section id="ai-visibility" title="AI Visibility Score" icon={Sparkles} desc="AI cevaplarında marka görünürlüğü (sentetik) + gerçek AI trafiğiyle (GA4) eşlenik" accent={C.purple}>
        <Card><CardContent className="p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
            <div className="text-sm text-muted-foreground">
              {vis?.latest
                ? <>Son analiz: <b className="text-foreground">{new Date(vis.latest.runAt).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" })}</b> · {vis.latest.meta.topicCount} konu × {vis.latest.meta.models.length} model · marka: <b className="text-foreground">{vis.latest.brand}</b>{vis.latest.meta.lang && <> · pazar: <b className="text-foreground">{({ tr: "🇹🇷 Türkçe", en: "🌍 English", ar: "🇸🇦 العربية" } as Record<string, string>)[vis.latest.meta.lang] ?? vis.latest.meta.lang}</b></>}</>
                : "Henüz analiz çalıştırılmadı. AI motorlarına sitenin konularında sorular sorulup markanın anılma oranı ölçülür (~750 token, ~1 dk)."}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* Pazar/dil seçici — hangi pazarın AI cevaplarında ölçülecek */}
              <div className="flex items-center rounded-lg border p-0.5" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
                {([{ k: "auto", l: "Oto" }, { k: "tr", l: "🇹🇷 TR" }, { k: "en", l: "🌍 EN" }, { k: "ar", l: "🇸🇦 AR" }] as const).map((o) => (
                  <button key={o.k} onClick={() => setAiMarket(o.k)} title="AI sorularının pazarı/dili" className="text-xs px-2 py-1 rounded-md transition" style={aiMarket === o.k ? { background: `${C.purple}33`, color: "#d42b29" } : { color: "#70706c" }}>{o.l}</button>
                ))}
              </div>
              <Button size="sm" disabled={!vis?.configured || runVis.isPending} onClick={async () => { if (await confirmCost("ai-visibility")) runVis.mutate(); }} className="gap-1.5" style={{ background: C.purple, color: "#fff" }}>
                {runVis.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : vis?.latest ? <RefreshCw className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
                {runVis.isPending ? "Analiz ediliyor…" : vis?.latest ? "Yeniden Analiz Et" : "Analiz Et"}
              </Button>
            </div>
          </div>
          {!vis?.configured && <p className="text-xs" style={{ color: C.amber }}>DataForSEO kimlik bilgileri tanımlı değil (.env: DATAFORSEO_LOGIN/PASSWORD).</p>}
          {runVis.isError && <p className="text-xs text-red-400">{(runVis.error as Error)?.message}</p>}
        </CardContent></Card>

        {runVis.isPending && <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-10"><Loader2 className="w-4 h-4 animate-spin" /> AI motorlarına sorular soruluyor ve cevaplar analiz ediliyor… (~1 dk)</div>}

        {!runVis.isPending && vis?.latest && (<>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            <Card><CardContent className="p-4">
              {(() => {
                // BİRLEŞİK SKOR: sentetik anılma (%60) + gerçek GA4 AI trafiği (%40; 50+ ziyaret = tam puan)
                // — kullanıcı isteği: skor GA4 gerçeğiyle EŞLENİK çalışsın, sentetik 0 diye 0 görünmesin.
                const realTotal = t.aiSummary?.totalUsers ?? 0;
                const trafficScore = Math.min(100, realTotal * 2);
                const combined = Math.round(0.6 * vis.latest.visibilityScore + 0.4 * trafficScore);
                return (<>
                  <div className="flex items-center gap-2 mb-2"><Gauge className="w-4 h-4" style={{ color: C.purple }} /><span className="font-semibold text-sm">AI Visibility Score</span><span className="text-[10px] text-muted-foreground">(birleşik)</span></div>
                  <div className="flex items-end gap-2"><span className="text-4xl font-bold tabular-nums" style={{ color: C.purple }}>{combined}</span><span className="text-sm text-muted-foreground mb-1">/100</span></div>
                  <div className="h-2 rounded-full bg-black/5 mt-2 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${combined}%`, background: C.purple }} /></div>
                  <div className="text-[10px] text-muted-foreground mt-1.5">= %60 sentetik anılma ({vis.latest.visibilityScore}) + %40 gerçek AI trafiği ({trafficScore})</div>
                </>);
              })()}
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div><div className="text-sm font-semibold tabular-nums">%{vis.latest.sov}</div><div className="text-[10px] text-muted-foreground">Share of Voice</div></div>
                <div><div className="text-sm font-semibold tabular-nums">%{vis.latest.mentionRate}</div><div className="text-[10px] text-muted-foreground">Anılma oranı</div></div>
                <div><div className="text-sm font-semibold tabular-nums">{vis.latest.meta.avgRank || "—"}</div><div className="text-[10px] text-muted-foreground">Ort. sıra</div></div>
              </div>
              {/* Sentetik skor ↔ gerçek GA4 AI trafiği eşlemesi */}
              <div className="mt-3 pt-3 border-t flex items-center justify-between" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" style={{ color: C.cyan }} />Gerçek AI trafiği (GA4)</span>
                <span className="text-sm font-bold tabular-nums" style={{ color: (t.aiSummary?.totalUsers ?? 0) > 0 ? C.cyan : "#70706c" }}>{fmt(t.aiSummary?.totalUsers ?? 0)} ziyaret</span>
              </div>
            </CardContent></Card>
            <Card className="lg:col-span-2"><CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4" style={{ color: C.purple }} /><span className="font-semibold text-sm">Model Bazlı Görünürlük</span><span className="text-[11px] text-muted-foreground">— sentetik anılma + gerçek GA4 ziyaret</span></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {vis.latest.byModel.map((m) => {
                  const realUsers = (t.aiByEngine ?? []).find((e) => e.engine === m.model)?.users ?? 0;
                  // Model bazlı BİRLEŞİK skor: sentetik anılma (%60) + o motordan gelen gerçek ziyaret (%40)
                  const combined = Math.round(0.6 * m.visibilityScore + 0.4 * Math.min(100, realUsers * 2));
                  return (
                    <div key={m.model} className="text-center p-3 rounded-lg" style={{ background: "rgba(0,0,0,0.03)" }}>
                      <div className="text-2xl font-bold tabular-nums" style={{ color: combined > 0 ? C.green : "#70706c" }}>{combined}</div>
                      <div className="text-xs font-medium">{m.model}</div>
                      <div className="text-[10px] text-muted-foreground">sentetik {m.visibilityScore} · anılma %{m.mentionRate}</div>
                      <div className="text-[10px] mt-1.5 pt-1.5 border-t" style={{ borderColor: "rgba(0,0,0,0.06)", color: realUsers > 0 ? C.cyan : "#70706c" }}>
                        {realUsers > 0 ? <><b>{fmt(realUsers)}</b> gerçek ziyaret</> : "gerçek ziyaret yok"}
                      </div>
                    </div>
                  );
                })}
              </div>
              {(() => {
                const realTotal = t.aiSummary?.totalUsers ?? 0;
                if (vis.latest.visibilityScore === 0 && realTotal > 0)
                  return <p className="text-[11px] mt-3 p-2 rounded" style={{ background: "rgba(6,182,212,0.08)", color: "#b12220" }}>🔎 <b>Çelişki çözümü:</b> Sentetik testte jenerik konularda anılmıyorsun (skor 0), AMA GA4'te AI'dan <b>{fmt(realTotal)} gerçek ziyaret</b> var. Demek ki spesifik/uzun-kuyruk sorgularda AI seni öneriyor — sadece bu test konularında değil. <b>Gerçek veri esastır.</b> Daha yüksek skor için bu test konularını da kazanmak hedef.</p>;
                if (vis.latest.visibilityScore === 0)
                  return <p className="text-[11px] text-muted-foreground mt-3">⚠️ Markan AI cevaplarında henüz anılmıyor ve GA4'te de AI trafiği görünmüyor — aşağıdaki "Top Brands" hangi rakiplerin baskın olduğunu gösterir.</p>;
                return null;
              })()}
            </CardContent></Card>
          </div>
          {vis.trend.length > 1 && (
            <Panel title="Visibility Trend (zaman içinde)" icon={TrendingUp} connected color={C.purple} className="mt-4">
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={vis.trend.map((d) => ({ date: new Date(d.runAt).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" }), score: d.visibilityScore, sov: d.sov }))}>
                    <defs><linearGradient id="visG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.purple} stopOpacity={0.35} /><stop offset="100%" stopColor={C.purple} stopOpacity={0} /></linearGradient></defs>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <RTooltip contentStyle={{ background: "#ffffff", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 8, fontSize: 12 }} />
                    <Area type="monotone" dataKey="score" name="Visibility" stroke={C.purple} fill="url(#visG)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          )}
        </>)}
      </Section>

      {/* ════ SHARE OF VOICE & TOP BRANDS ════ */}
      <Section id="ai-sov" title="Share of Voice & Top Brands" icon={Trophy} desc="AI cevaplarında en görünür markalar — bu alanda rakiplerin hakimiyeti" accent={C.amber}>
        <Panel title="AI Cevaplarında En Görünür Markalar" icon={Trophy} connected={!!vis?.latest?.topBrands?.length} color={C.amber} hint="Analiz çalıştırılınca markalar burada listelenir.">
          {vis?.latest && (() => {
            const max = Math.max(...vis.latest.topBrands.map((b) => b.mentions), 1);
            return (
              <div className="space-y-2.5">{vis.latest.topBrands.map((b, i) => (
                <div key={b.name} className="space-y-1">
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate flex-1 min-w-0 flex items-center gap-1.5"><span className="text-muted-foreground">{i + 1}.</span> {b.name}{b.isUs && <span className="text-[10px] px-1.5 py-0.5 rounded shrink-0" style={{ background: `${C.green}22`, color: C.green }}>BİZ</span>}</span>
                    <span className="text-muted-foreground tabular-nums shrink-0">{b.mentions}x</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-black/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(b.mentions / max) * 100}%`, background: b.isUs ? C.green : C.amber }} /></div>
                </div>
              ))}</div>
            );
          })()}
        </Panel>
      </Section>

      {/* ════ AI SEO ALERTS ════ */}
      <Section id="ai-alerts" title="AI SEO Alerts" icon={AlertTriangle} desc="AI mention artış/kayıp (analiz geçmişine göre)" accent={C.red}>
        {vis?.trend && vis.trend.length >= 2 ? (() => {
          const last = vis.trend[vis.trend.length - 1]!, prev = vis.trend[vis.trend.length - 2]!;
          const dScore = last.visibilityScore - prev.visibilityScore, dSov = last.sov - prev.sov;
          const al: { c: string; t: string }[] = [];
          if (dScore >= 5) al.push({ c: C.green, t: `AI görünürlük arttı: +${dScore} puan (önceki analize göre).` });
          if (dScore <= -5) al.push({ c: C.red, t: `AI görünürlük düştü: ${dScore} puan.` });
          if (dSov >= 3) al.push({ c: C.green, t: `Share of Voice arttı: +%${dSov}.` });
          if (dSov <= -3) al.push({ c: C.red, t: `Share of Voice düştü: %${dSov}.` });
          return al.length
            ? <div className="space-y-2">{al.map((a, i) => <div key={i} className="p-3 rounded-lg text-sm font-medium" style={{ background: `${a.c}15`, color: a.c }}>{a.t}</div>)}</div>
            : <Card><CardContent className="p-6 text-center text-xs text-muted-foreground">Son iki analiz arasında belirgin değişiklik yok. ✅</CardContent></Card>;
        })() : (
          <Card style={{ borderStyle: "dashed", borderColor: "rgba(239,68,68,0.3)" }}><CardContent className="p-6 text-center text-xs text-muted-foreground">
            En az 2 analiz çalıştırınca AI görünürlük artış/kayıp uyarıları burada belirir. (Şu an {vis?.trend?.length ?? 0} analiz kaydı var — düzenli aralıklarla "Analiz Et"e basarak trend oluştur.)
          </CardContent></Card>
        )}
      </Section>
      </>)}
    </div>
  );
}
